import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import {
  getCart,
  clearCart,
  getCartTotal,
  CartItem,
} from '../../Services/cartService';
import { calculateDiscountedPrice, hasDiscount } from '../../Services/productService';
import { useCompany } from '../../Contexts/CompanyContext';
import { CheckoutService } from '../../Services/checkoutService';
import {
  getCountries,
  getStatesByCountry,
  getShippingInfo,
  clearShippingInfo,
  qualifiesForFreeShipping,
  Country,
  StateProvince,
} from '../../Services/shippingService';
import HeaderSpacer from '../Header/HeaderSpacer';

interface ShippingAddress {
  fullName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

// ── Payment proof types ───────────────────────────────────────────────────────
const PAYMENT_PROOF_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
const PAYMENT_PROOF_MAX_MB = 10;

// Payment methods that go through PayHere gateway
const PAYHERE_METHODS = ['credit_card', 'paypal'];

// ── localStorage keys ─────────────────────────────────────────────────────────
const CART_BACKUP_KEY = 'payhere_cart_backup';
const FORM_BACKUP_KEY = 'payhere_form_backup';

// ── Cart backup helpers ───────────────────────────────────────────────────────
interface CartBackup {
  cartItems: CartItem[];
  savedAt: number;
}

const saveCartBackup = (items: CartItem[]): void => {
  try {
    const backup: CartBackup = { cartItems: items, savedAt: Date.now() };
    localStorage.setItem(CART_BACKUP_KEY, JSON.stringify(backup));
  } catch {
    // localStorage may be unavailable — silent fail
  }
};

const restoreCartBackup = (): CartItem[] | null => {
  try {
    const raw = localStorage.getItem(CART_BACKUP_KEY);
    if (!raw) return null;
    const backup: CartBackup = JSON.parse(raw);
    // Only restore backups that are less than 2 hours old
    if (Date.now() - backup.savedAt > 2 * 60 * 60 * 1000) {
      localStorage.removeItem(CART_BACKUP_KEY);
      return null;
    }
    return backup.cartItems;
  } catch {
    return null;
  }
};

const clearCartBackup = (): void => {
  try {
    localStorage.removeItem(CART_BACKUP_KEY);
  } catch {
    // silent
  }
};

// ── Form backup helpers ───────────────────────────────────────────────────────
interface FormBackup {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerWhatsApp: string;
  shippingAddress: ShippingAddress;
  billingAddress: ShippingAddress;
  useSameAddress: boolean;
  paymentMethod: string;
  orderNotes: string;
  // Country/state IDs so we can re-select them after countries load
  selectedShippingCountryId: string | null;
  selectedShippingStateId: string | null;
  selectedBillingCountryId: string | null;
  selectedBillingStateId: string | null;
  savedAt: number;
}

const saveFormBackup = (data: Omit<FormBackup, 'savedAt'>): void => {
  try {
    const backup: FormBackup = { ...data, savedAt: Date.now() };
    localStorage.setItem(FORM_BACKUP_KEY, JSON.stringify(backup));
  } catch {
    // silent
  }
};

const restoreFormBackup = (): FormBackup | null => {
  try {
    const raw = localStorage.getItem(FORM_BACKUP_KEY);
    if (!raw) return null;
    const backup: FormBackup = JSON.parse(raw);
    // Only restore backups less than 2 hours old
    if (Date.now() - backup.savedAt > 2 * 60 * 60 * 1000) {
      localStorage.removeItem(FORM_BACKUP_KEY);
      return null;
    }
    return backup;
  } catch {
    return null;
  }
};

const clearFormBackup = (): void => {
  try {
    localStorage.removeItem(FORM_BACKUP_KEY);
  } catch {
    // silent
  }
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { formatPrice, calculateTax, taxPercentage } = useCompany();

  // ── Country / state data ──────────────────────────────────────────────────
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [shippingStates, setShippingStates] = useState<StateProvince[]>([]);
  const [billingStates, setBillingStates] = useState<StateProvince[]>([]);

  const [selectedShippingCountry, setSelectedShippingCountry] = useState<Country | null>(null);
  const [selectedShippingState, setSelectedShippingState] = useState<StateProvince | null>(null);
  const [selectedBillingCountry, setSelectedBillingCountry] = useState<Country | null>(null);
  const [selectedBillingState, setSelectedBillingState] = useState<StateProvince | null>(null);

  // ── Customer fields ───────────────────────────────────────────────────────
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerWhatsApp, setCustomerWhatsApp] = useState('');

  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    fullName: '', addressLine1: '', addressLine2: '',
    city: '', state: '', postalCode: '', country: '', phoneNumber: '',
  });
  const [billingAddress, setBillingAddress] = useState<ShippingAddress>({
    fullName: '', addressLine1: '', addressLine2: '',
    city: '', state: '', postalCode: '', country: '', phoneNumber: '',
  });

  const [useSameAddress, setUseSameAddress] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [orderNotes, setOrderNotes] = useState('');

  // ── Shipping figures ──────────────────────────────────────────────────────
  const [shippingCost, setShippingCost] = useState(0);
  const [freeShippingLimit, setFreeShippingLimit] = useState(0);

  // ── Payment proof state (bank_transfer only) ──────────────────────────────
  const [pendingProofFile, setPendingProofFile] = useState<File | null>(null);
  const [pendingProofPreview, setPendingProofPreview] = useState<string | null>(null);
  const [proofFileError, setProofFileError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const proofInputRef = useRef<HTMLInputElement>(null);

  // Derived flags
  const isBankTransfer = paymentMethod === 'bank_transfer';
  const isPayhereMethod = PAYHERE_METHODS.includes(paymentMethod);

  // ── Toast notification state ─────────────────────────────────────────────
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({
    message: '', visible: false,
  });

  const showToast = (message: string) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast({ message: '', visible: false }), 5000);
  };

  // ── Track whether a form backup has been applied ──────────────────────────
  // We use a ref so it doesn't cause re-renders and is stable across effects.
  const formBackupApplied = useRef(false);
  // Store pending country/state IDs to apply once countries finish loading
  const pendingFormRestore = useRef<{
    shippingCountryId: string | null;
    shippingStateId: string | null;
    billingCountryId: string | null;
    billingStateId: string | null;
  } | null>(null);

  // ── Detect payment_cancelled redirect back from PayHere ───────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment_cancelled') === 'true') {
      // Show a single toast — no banner, no red error box
      showToast('Payment was cancelled. Please try again or choose a different payment method.');

      // Restore form state from backup (filled before the PayHere redirect)
      const formBackup = restoreFormBackup();
      if (formBackup && !formBackupApplied.current) {
        formBackupApplied.current = true;

        setCustomerName(formBackup.customerName);
        setCustomerEmail(formBackup.customerEmail);
        setCustomerPhone(formBackup.customerPhone);
        setCustomerWhatsApp(formBackup.customerWhatsApp);
        setShippingAddress(formBackup.shippingAddress);
        setBillingAddress(formBackup.billingAddress);
        setUseSameAddress(formBackup.useSameAddress);
        setPaymentMethod(formBackup.paymentMethod);
        setOrderNotes(formBackup.orderNotes);

        // Store country/state IDs to re-apply once the countries list loads
        pendingFormRestore.current = {
          shippingCountryId: formBackup.selectedShippingCountryId,
          shippingStateId: formBackup.selectedShippingStateId,
          billingCountryId: formBackup.selectedBillingCountryId,
          billingStateId: formBackup.selectedBillingStateId,
        };
      }

      // The cart was NOT cleared before the PayHere redirect so it is still
      // intact in localStorage — no restore needed.
      // Just clean up the backup (written as a mid-session refresh safety net).
      clearCartBackup();

      // Clean the URL without reloading
      window.history.replaceState({}, '', '/checkout');
    }
  }, [location.search]);

  // ── Clean up object URL on unmount ────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);
    };
  }, [pendingProofPreview]);

  // ── When payment method changes away from bank_transfer, clear proof ──────
  useEffect(() => {
    if (!isBankTransfer) {
      setPendingProofFile(null);
      setProofFileError('');
      setIsDragOver(false);
      if (pendingProofPreview) {
        URL.revokeObjectURL(pendingProofPreview);
        setPendingProofPreview(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBankTransfer]);

  // ── Load countries then patch saved shipping info ─────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingCountries(true);
        const loadedCountries = await getCountries();
        setCountries(loadedCountries);

        // ── Re-apply country/state selections from form backup ────────────
        // This runs after payment_cancelled restore set pendingFormRestore.
        if (pendingFormRestore.current) {
          const {
            shippingCountryId,
            shippingStateId,
            billingCountryId,
            billingStateId,
          } = pendingFormRestore.current;

          if (shippingCountryId) {
            const countryObj = loadedCountries.find(c => c.id === shippingCountryId) ?? null;
            if (countryObj) {
              const countryStates = getStatesByCountry(countryObj.id, loadedCountries);
              setShippingStates(countryStates);
              setSelectedShippingCountry(countryObj);

              if (shippingStateId) {
                const stateObj = countryStates.find(s => s.id === shippingStateId) ?? null;
                if (stateObj) setSelectedShippingState(stateObj);
              }
            }
          }

          if (!formBackup_useSameAddress_ref.current && billingCountryId) {
            const countryObj = loadedCountries.find(c => c.id === billingCountryId) ?? null;
            if (countryObj) {
              const countryStates = getStatesByCountry(countryObj.id, loadedCountries);
              setBillingStates(countryStates);
              setSelectedBillingCountry(countryObj);

              if (billingStateId) {
                const stateObj = countryStates.find(s => s.id === billingStateId) ?? null;
                if (stateObj) setSelectedBillingState(stateObj);
              }
            }
          }

          // Form backup has been fully applied — clear it from storage
          clearFormBackup();
          pendingFormRestore.current = null;
          return; // Skip the regular shippingInfo restore below
        }

        // ── Regular flow: restore from shippingService ────────────────────
        const saved = getShippingInfo();
        if (!saved) return;

        setShippingCost(saved.shippingCost);
        setFreeShippingLimit(saved.freeShippingLimit ?? 0);

        if (!saved.countryId) return;

        const countryObj = loadedCountries.find(c => c.id === saved.countryId) ?? null;

        if (countryObj) {
          const countryStates = getStatesByCountry(countryObj.id, loadedCountries);
          setShippingStates(countryStates);
          setSelectedShippingCountry(countryObj);

          if (saved.stateId) {
            const stateObj = countryStates.find(s => s.id === saved.stateId) ?? null;
            if (stateObj) {
              setSelectedShippingState(stateObj);
              setShippingAddress(prev => ({
                ...prev,
                country: countryObj.name,
                state: stateObj.name,
                postalCode: saved.postalCode ?? '',
              }));
            } else {
              setShippingAddress(prev => ({
                ...prev,
                country: countryObj.name,
                state: saved.stateName ?? '',
                postalCode: saved.postalCode ?? '',
              }));
            }
          } else {
            setShippingAddress(prev => ({
              ...prev,
              country: countryObj.name,
              postalCode: saved.postalCode ?? '',
            }));
          }
        } else {
          setShippingAddress(prev => ({
            ...prev,
            country: saved.countryName ?? '',
            state: saved.stateName ?? '',
            postalCode: saved.postalCode ?? '',
          }));
        }
      } catch (err) {
        console.error('Error loading countries:', err);
      } finally {
        setLoadingCountries(false);
      }
    })();
  }, []);

  // ── Helper ref to pass useSameAddress into the async country loader ───────
  // (avoids stale closure — the async effect runs once so it can't read
  //  the latest state directly)
  const formBackup_useSameAddress_ref = useRef(useSameAddress);
  useEffect(() => {
    formBackup_useSameAddress_ref.current = useSameAddress;
  }, [useSameAddress]);

  // ── Shipping states update when country changes ───────────────────────────
  const [countriesLoaded, setCountriesLoaded] = useState(false);
  useEffect(() => {
    if (!countriesLoaded) {
      if (countries.length > 0) setCountriesLoaded(true);
      return;
    }
    if (selectedShippingCountry) {
      const newStates = getStatesByCountry(selectedShippingCountry.id, countries);
      setShippingStates(newStates);
      setSelectedShippingState(prev => {
        if (prev && newStates.find(s => s.id === prev.id)) return prev;
        setShippingAddress(p => ({ ...p, state: '' }));
        return null;
      });
    } else {
      setShippingStates([]);
      setSelectedShippingState(null);
      setShippingAddress(prev => ({ ...prev, state: '' }));
    }
  }, [selectedShippingCountry, countries, countriesLoaded]);

  // ── Billing states update when country changes ────────────────────────────
  useEffect(() => {
    if (selectedBillingCountry) {
      setBillingStates(getStatesByCountry(selectedBillingCountry.id, countries));
      setSelectedBillingState(null);
      setBillingAddress(prev => ({ ...prev, state: '' }));
    } else {
      setBillingStates([]);
      setSelectedBillingState(null);
      setBillingAddress(prev => ({ ...prev, state: '' }));
    }
  }, [selectedBillingCountry, countries]);

  // ── Load cart ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const items = getCart();
    if (items.length === 0) {
      // Edge case: if the user manually refreshed mid-payment, the cart key in
      // localStorage may have been wiped by the browser. Restore from backup.
      const backup = restoreCartBackup();
      if (backup && backup.length > 0) {
        try {
          localStorage.setItem('cart', JSON.stringify(backup));
        } catch { /* silent */ }
        setCartItems(backup);
        return;
      }
      // Cart is genuinely empty — redirect to cart page
      navigate('/cart');
      return;
    }
    setCartItems(items);
  }, [navigate]);

  // ── Sync billing address when "same as shipping" ──────────────────────────
  useEffect(() => {
    if (useSameAddress) {
      setBillingAddress(shippingAddress);
      setSelectedBillingCountry(selectedShippingCountry);
      setSelectedBillingState(selectedShippingState);
      setBillingStates(shippingStates);
    }
  }, [useSameAddress, shippingAddress, selectedShippingCountry, selectedShippingState, shippingStates]);

  // ── Country / state change handlers ──────────────────────────────────────
  const handleShippingCountryChange = (countryId: string) => {
    const country = countries.find(c => c.id === countryId) ?? null;
    setSelectedShippingCountry(country);
    setShippingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
  };

  const handleShippingStateChange = (stateId: string) => {
    const state = shippingStates.find(s => s.id === stateId) ?? null;
    setSelectedShippingState(state);
    setShippingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
  };

  const handleBillingCountryChange = (countryId: string) => {
    const country = countries.find(c => c.id === countryId) ?? null;
    setSelectedBillingCountry(country);
    setBillingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
  };

  const handleBillingStateChange = (stateId: string) => {
    const state = billingStates.find(s => s.id === stateId) ?? null;
    setSelectedBillingState(state);
    setBillingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
  };

  const handleShippingAddressChange = (field: keyof ShippingAddress, value: string) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleBillingAddressChange = (field: keyof ShippingAddress, value: string) => {
    setBillingAddress(prev => ({ ...prev, [field]: value }));
  };

  // ── Payment proof handlers ────────────────────────────────────────────────
  const processProofFile = (file: File) => {
    setProofFileError('');

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setProofFileError('Only images (JPEG, PNG, WebP, GIF) and PDF files are allowed.');
      return;
    }

    const maxBytes = PAYMENT_PROOF_MAX_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setProofFileError(`File size must be under ${PAYMENT_PROOF_MAX_MB} MB.`);
      return;
    }

    if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);

    setPendingProofFile(file);

    if (file.type !== 'application/pdf') {
      setPendingProofPreview(URL.createObjectURL(file));
    } else {
      setPendingProofPreview(null);
    }

    if (proofInputRef.current) proofInputRef.current.value = '';
  };

  const handleProofFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processProofFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processProofFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleClearProof = () => {
    setPendingProofFile(null);
    setProofFileError('');
    if (pendingProofPreview) {
      URL.revokeObjectURL(pendingProofPreview);
      setPendingProofPreview(null);
    }
  };

  // ── Discount calculation ──────────────────────────────────────────────────
  const computeCartDiscountAmount = (items: CartItem[]): number =>
    items.reduce((total, item) => {
      if (!hasDiscount(item.discount)) return total;
      const discounted = calculateDiscountedPrice(item.price, item.discount);
      return total + (item.price - discounted) * item.quantity;
    }, 0);

  // ── Order-summary figures ─────────────────────────────────────────────────
  const subtotal = getCartTotal();
  const discountAmount = computeCartDiscountAmount(cartItems);
  const taxAmount = calculateTax(subtotal);
  const isFreeShipping = qualifiesForFreeShipping(subtotal, freeShippingLimit);
  const finalShipping = isFreeShipping ? 0 : shippingCost;
  const totalAmount = subtotal + taxAmount + finalShipping;

  // ── Build shared order data object ────────────────────────────────────────
  const buildOrderData = () => ({
    customerName,
    customerEmail,
    customerPhone,
    customerWhatsApp,
    items: cartItems.map(item => ({
      productId: item.productId,
      variantId: item.variantId || '',
      quantity: item.quantity.toString(),
    })),
    subtotal: subtotal.toString(),
    taxAmount: taxAmount.toString(),
    shippingCost: finalShipping.toString(),
    discountAmount: discountAmount.toString(),
    status: 'pending' as const,
    paymentStatus: 'pending' as const,
    paymentMethod: paymentMethod as
      'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery',
    shippingAddress,
    billingAddress: useSameAddress ? shippingAddress : billingAddress,
    notes: orderNotes,
    trackingNumber: '',
    estimatedDelivery: '',
    allocatedWarehouseId: '',
  });

  // ── Form validation ───────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    if (!customerName.trim()) { setError('Please enter your name'); return false; }
    if (!customerEmail.trim()) { setError('Please enter your email'); return false; }
    if (!customerPhone.trim()) { setError('Please enter your phone number'); return false; }
    if (!shippingAddress.fullName.trim()) { setError('Please enter shipping address full name'); return false; }
    if (!shippingAddress.addressLine1.trim()) { setError('Please enter shipping address'); return false; }
    if (!shippingAddress.city.trim()) { setError('Please enter city'); return false; }
    if (!shippingAddress.postalCode.trim()) { setError('Please enter postal code'); return false; }
    if (!shippingAddress.phoneNumber.trim()) { setError('Please enter phone number for shipping'); return false; }

    if (isBankTransfer && !pendingProofFile) {
      setError('Please upload your bank transfer receipt to proceed.');
      return false;
    }

    return true;
  };

  // ── Clear all form state (called only on successful order) ────────────────
  const clearFormState = () => {
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomerWhatsApp('');
    setShippingAddress({
      fullName: '', addressLine1: '', addressLine2: '',
      city: '', state: '', postalCode: '', country: '', phoneNumber: '',
    });
    setBillingAddress({
      fullName: '', addressLine1: '', addressLine2: '',
      city: '', state: '', postalCode: '', country: '', phoneNumber: '',
    });
    setUseSameAddress(true);
    setPaymentMethod('credit_card');
    setOrderNotes('');
    setSelectedShippingCountry(null);
    setSelectedShippingState(null);
    setSelectedBillingCountry(null);
    setSelectedBillingState(null);
    setShippingStates([]);
    setBillingStates([]);
    setPendingProofFile(null);
    setProofFileError('');
    if (pendingProofPreview) {
      URL.revokeObjectURL(pendingProofPreview);
      setPendingProofPreview(null);
    }
  };

  // ── Place order ───────────────────────────────────────────────────────────
  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const orderData = buildOrderData();

      // ── PayHere flow (credit_card / paypal) ───────────────────────────────
      // IMPORTANT: No order is created in Firestore here. The Cloud Function
      // stores a pending order in `pendingOrders`. The real order in `orders`
      // is ONLY created by the payhereNotify webhook after successful payment.
      // This means failed/cancelled payments NEVER produce an order record.
      if (isPayhereMethod) {
        const result = await CheckoutService.initiatePayment(orderData);

        if (!result.success || !result.payhereParams || !result.pendingOrderId) {
          setError(result.message || 'Failed to initiate payment. Please try again.');
          setLoading(false);
          return;
        }

        // Save a cart backup as a safety net for mid-session page refreshes
        // (e.g. user manually refreshes during payment). The cart itself is
        // NOT cleared here — it stays intact until the OrderConfirmation page
        // confirms paymentStatus='paid'. This way:
        //   • Payment success → OrderConfirmation clears cart + shippingInfo
        //   • Payment failed  → user returns to /checkout, cart still intact
        //   • Payment cancel  → cancel_url returns to /checkout, cart still intact
        saveCartBackup(cartItems);

        // Save form state so it can be restored if the user returns after
        // cancelling payment. Form is only cleared on successful order.
        saveFormBackup({
          customerName,
          customerEmail,
          customerPhone,
          customerWhatsApp,
          shippingAddress,
          billingAddress,
          useSameAddress,
          paymentMethod,
          orderNotes,
          selectedShippingCountryId: selectedShippingCountry?.id ?? null,
          selectedShippingStateId: selectedShippingState?.id ?? null,
          selectedBillingCountryId: selectedBillingCountry?.id ?? null,
          selectedBillingStateId: selectedBillingState?.id ?? null,
        });

        // Redirect to PayHere (browser leaves this page)
        CheckoutService.redirectToPayhere(result.payhereParams);

        // NOTE: Code below this line will NOT execute after the redirect.
        return;
      }

      // ── Bank Transfer flow ────────────────────────────────────────────────
      if (isBankTransfer) {
        const result = await CheckoutService.createOrder(orderData);

        if (!result.success || !result.orderId) {
          setError(result.message || 'Failed to place order');
          return;
        }

        const orderId = result.orderId;

        if (pendingProofFile) {
          try {
            const proofResult = await CheckoutService.uploadPaymentProof(
              pendingProofFile,
              orderId
            );
            await CheckoutService.savePaymentProofToOrder(orderId, proofResult);
          } catch (uploadErr) {
            console.error('Payment proof upload failed:', uploadErr);
            // Order is created — don't block confirmation
          }
        }

        // Success — clear everything including form state and both backups
        setSuccess(true);
        clearCart();
        clearShippingInfo();
        clearCartBackup();
        clearFormBackup();
        clearFormState();
        setTimeout(() => navigate(`/order-confirmation/${orderId}`), 2000);
        return;
      }

      // ── Cash on Delivery flow ─────────────────────────────────────────────
      const result = await CheckoutService.createOrder(orderData);

      if (!result.success || !result.orderId) {
        setError(result.message || 'Failed to place order');
        return;
      }

      // Success — clear everything including form state and both backups
      setSuccess(true);
      clearCart();
      clearShippingInfo();
      clearCartBackup();
      clearFormBackup();
      clearFormState();
      setTimeout(() => navigate(`/order-confirmation/${result.orderId}`), 2000);

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to place order. Please try again.');
    } finally {
      // Only clear loading for non-PayHere flows (PayHere redirects away)
      if (!isPayhereMethod) {
        setLoading(false);
      }
    }
  };

  // ── Shared select style ───────────────────────────────────────────────────
  const selectStyle: React.CSSProperties = {
    width: '100%', padding: '10px 15px', border: 'none',
    background: 'transparent', cursor: 'pointer', fontSize: '13px',
    color: '#555', appearance: 'none',
    fontFamily: 'Poppins-Regular, sans-serif', outline: 'none',
  };

  // ── Inline payment proof upload (shown inside the Bank Transfer card) ─────
  const renderInlineProofUpload = () => (
    <div style={{ marginTop: '12px' }}>
      <input
        ref={proofInputRef}
        type="file"
        accept={PAYMENT_PROOF_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleProofFileSelect}
      />

      {proofFileError && (
        <div style={{
          marginBottom: '10px', padding: '8px 12px',
          backgroundColor: '#fff5f5', border: '1px solid #f5c6cb',
          borderRadius: '6px', color: '#721c24', fontSize: '12px',
          fontFamily: 'Poppins-Regular, sans-serif',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <i className="zmdi zmdi-alert-circle" style={{ fontSize: '14px', flexShrink: 0 }} />
          {proofFileError}
        </div>
      )}

      {pendingProofFile ? (
        <div style={{
          border: '1px solid #b8f0c8', borderRadius: '8px',
          backgroundColor: '#f0fbf4', padding: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            {pendingProofFile.type !== 'application/pdf' && pendingProofPreview ? (
              <div style={{
                width: '42px', height: '42px', borderRadius: '6px',
                overflow: 'hidden', border: '1px solid #c3e6cb', flexShrink: 0,
              }}>
                <img src={pendingProofPreview} alt="Receipt preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            ) : (
              <div style={{
                width: '42px', height: '42px', borderRadius: '6px',
                backgroundColor: '#fff0f0', border: '1px solid #f5c6cb',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <i className="zmdi zmdi-file-text" style={{ fontSize: '22px', color: '#c62828' }} />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: '0 0 3px 0', fontSize: '12px', fontWeight: 600,
                color: '#2d6a4f', overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', fontFamily: 'Poppins-Regular, sans-serif',
              }}>
                {pendingProofFile.name}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <i className="zmdi zmdi-check-circle" style={{ fontSize: '12px', color: '#28a745' }} />
                <span style={{ fontSize: '11px', color: '#28a745', fontFamily: 'Poppins-Regular, sans-serif' }}>
                  Ready · {(pendingProofFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #c3e6cb', paddingTop: '8px', display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => proofInputRef.current?.click()}
              style={{
                flex: 1, background: '#fff', border: '1px solid #aed6bc',
                borderRadius: '6px', padding: '6px 0', cursor: 'pointer',
                fontSize: '12px', color: '#2d6a4f', fontFamily: 'Poppins-Regular, sans-serif',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '4px', transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#e8f5ee')}
              onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
              <i className="zmdi zmdi-refresh" style={{ fontSize: '13px' }} /> Change
            </button>
            <button type="button" onClick={handleClearProof}
              style={{
                flex: 1, background: '#fff', border: '1px solid #e0e0e0',
                borderRadius: '6px', padding: '6px 0', cursor: 'pointer',
                fontSize: '12px', color: '#999', fontFamily: 'Poppins-Regular, sans-serif',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '4px', transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#fff5f5';
                e.currentTarget.style.borderColor = '#f5c6cb';
                e.currentTarget.style.color = '#c0392b';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.color = '#999';
              }}>
              <i className="zmdi zmdi-delete" style={{ fontSize: '13px' }} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => proofInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            border: isDragOver ? '2px dashed #717fe0' : '2px dashed #c5caef',
            borderRadius: '8px',
            backgroundColor: isDragOver ? '#eef0fd' : '#fafbff',
            padding: '16px 12px', textAlign: 'center', cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '36px', height: '36px', borderRadius: '50%',
            backgroundColor: isDragOver ? '#717fe0' : '#eef0fd',
            marginBottom: '8px', transition: 'all 0.2s',
          }}>
            <i className="zmdi zmdi-cloud-upload"
              style={{ fontSize: '18px', color: isDragOver ? '#fff' : '#717fe0' }} />
          </div>
          <p style={{
            margin: '0 0 3px 0', fontSize: '12px', fontWeight: 600,
            color: '#444', fontFamily: 'Poppins-Regular, sans-serif',
          }}>
            Drop your receipt here or{' '}
            <span style={{ color: '#717fe0', textDecoration: 'underline' }}>browse</span>
          </p>
          <p style={{ margin: 0, fontSize: '11px', color: '#999', fontFamily: 'Poppins-Regular, sans-serif' }}>
            JPEG · PNG · WebP · GIF · PDF — max {PAYMENT_PROOF_MAX_MB} MB
          </p>
        </div>
      )}

      <div style={{
        marginTop: '10px', padding: '8px 12px', backgroundColor: '#f0f4ff',
        borderLeft: '3px solid #717fe0', borderRadius: '0 4px 4px 0',
        display: 'flex', alignItems: 'flex-start', gap: '6px',
      }}>
        <i className="zmdi zmdi-info-outline"
          style={{ fontSize: '13px', color: '#717fe0', marginTop: '1px', flexShrink: 0 }} />
        <p style={{
          margin: 0, fontSize: '11px', color: '#666',
          fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.6',
        }}>
          Orders are processed after payment verification, typically within 1–2 business days.
        </p>
      </div>
    </div>
  );

  // ── PayHere info block (shown inside credit_card / paypal cards) ──────────
  const renderPayhereInfo = () => (
    <div style={{
      marginTop: '12px',
      padding: '12px 14px',
      backgroundColor: '#f8f9ff',
      borderRadius: '8px',
      border: '1px solid #e0e3f5',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <i className="zmdi zmdi-shield-check" style={{ fontSize: '16px', color: '#717fe0' }} />
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#444', fontFamily: 'Poppins-Regular, sans-serif' }}>
          Secured by PayHere
        </span>
      </div>
      <p style={{
        margin: 0, fontSize: '11px', color: '#666',
        fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.6',
      }}>
        You will be redirected to PayHere's secure payment page to complete your purchase.
        Your card details are never shared with us.
      </p>
    </div>
  );

  // ── Redirect loading overlay ──────────────────────────────────────────────
  const renderRedirectingOverlay = () => (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '40px 50px',
        textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        maxWidth: '360px', width: '90%',
      }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          border: '4px solid #f0f0f0', borderTop: '4px solid #717fe0',
          margin: '0 auto 20px', animation: 'spin 0.8s linear infinite',
        }} />
        <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#222', fontWeight: 700 }}>
          Redirecting to PayHere
        </h3>
        <p style={{ margin: 0, fontSize: '13px', color: '#888', lineHeight: '1.6' }}>
          Please wait while we redirect you to the secure payment page…
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── Success screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div>
        <Header />
        <HeaderSpacer />
        <div className="container" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="text-center">
            <i className="zmdi zmdi-check-circle" style={{ fontSize: '80px', color: '#28a745', marginBottom: '20px' }} />
            <h2 className="mtext-111 cl2 p-b-16">Order Placed Successfully!</h2>
            <p className="stext-113 cl6 p-b-26">Thank you for your order. You will receive a confirmation email shortly.</p>
            <p className="stext-113 cl6">Redirecting to order confirmation...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Determine submit button label / disabled state ────────────────────────
  const isButtonDisabled =
    loading ||
    (isBankTransfer && !pendingProofFile);

  const buttonLabel = loading && isPayhereMethod
    ? 'Redirecting to PayHere…'
    : loading
      ? 'Processing...'
      : isBankTransfer && !pendingProofFile
        ? 'Upload Receipt to Continue'
        : isPayhereMethod
          ? 'Pay with PayHere'
          : 'Place Order';

  return (
    <div>
      {/* Overlay while redirecting to PayHere */}
      {loading && isPayhereMethod && renderRedirectingOverlay()}

      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
          </a>
          <a href="/cart" className="stext-109 cl8 hov-cl1 trans-04">
            Shopping Cart <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
          </a>
          <span className="stext-109 cl4">Checkout</span>
        </div>
      </div>

      {/* ── Toast notification (payment cancelled / failed) ── */}
      {toast.visible && (
        <div style={{
          position: 'fixed', top: '30px', left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#1a1a2e', color: '#fff',
          padding: '14px 22px', borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          display: 'flex', alignItems: 'center', gap: '10px',
          zIndex: 99999, maxWidth: '480px', width: '90%',
          animation: 'toastIn 0.3s ease',
        }}>
          <i className="zmdi zmdi-alert-circle-o" style={{ fontSize: '20px', color: '#f39c12', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.5' }}>
            {toast.message}
          </span>
          <button
            onClick={() => setToast({ message: '', visible: false })}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              color: '#aaa', cursor: 'pointer', fontSize: '18px',
              lineHeight: 1, padding: '0 0 0 8px', flexShrink: 0,
            }}
          >×</button>
        </div>
      )}

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">
          {error && (
            <div style={{
              marginBottom: '30px', padding: '15px',
              backgroundColor: '#f8d7da', border: '1px solid #f5c6cb',
              borderRadius: '5px', color: '#721c24',
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handlePlaceOrder}>
            <div className="row">

              {/* ══════════════ LEFT ══════════════ */}
              <div className="col-lg-7 col-xl-8 m-lr-auto m-b-50">
                <div className="m-l-25 m-r--38 m-lr-0-xl">

                  {/* Customer Information */}
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
                    <h4 className="mtext-109 cl2 p-b-30">Customer Information</h4>

                    <div className="bor8 m-b-20 how-pos4-parent">
                      <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="text"
                        placeholder="Full Name *" value={customerName} required
                        onChange={e => setCustomerName(e.target.value)} />
                      <i className="zmdi zmdi-account how-pos4 pointer-none" />
                    </div>
                    <div className="bor8 m-b-20 how-pos4-parent">
                      <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="email"
                        placeholder="Email Address *" value={customerEmail} required
                        onChange={e => setCustomerEmail(e.target.value)} />
                      <i className="zmdi zmdi-email how-pos4 pointer-none" />
                    </div>
                    <div className="bor8 m-b-20 how-pos4-parent">
                      <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
                        placeholder="Phone Number *" value={customerPhone} required
                        onChange={e => setCustomerPhone(e.target.value)} />
                      <i className="zmdi zmdi-phone how-pos4 pointer-none" />
                    </div>
                    <div className="bor8 m-b-20 how-pos4-parent">
                      <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
                        placeholder="WhatsApp Number" value={customerWhatsApp}
                        onChange={e => setCustomerWhatsApp(e.target.value)} />
                      <i className="zmdi zmdi-whatsapp how-pos4 pointer-none" />
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
                    <h4 className="mtext-109 cl2 p-b-30">Shipping Address</h4>

                    <div className="bor8 m-b-20">
                      <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                        placeholder="Full Name *" value={shippingAddress.fullName} required
                        onChange={e => handleShippingAddressChange('fullName', e.target.value)} />
                    </div>
                    <div className="bor8 m-b-20">
                      <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                        placeholder="Address Line 1 *" value={shippingAddress.addressLine1} required
                        onChange={e => handleShippingAddressChange('addressLine1', e.target.value)} />
                    </div>
                    <div className="bor8 m-b-20">
                      <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                        placeholder="Address Line 2" value={shippingAddress.addressLine2}
                        onChange={e => handleShippingAddressChange('addressLine2', e.target.value)} />
                    </div>
                    <div className="bor8 m-b-20">
                      <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                        placeholder="City *" value={shippingAddress.city} required
                        onChange={e => handleShippingAddressChange('city', e.target.value)} />
                    </div>

                    {/* Country */}
                    <div className="rs1-select2 bor8 bg0 m-b-20">
                      {loadingCountries ? (
                        <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
                      ) : (
                        <select style={selectStyle}
                          value={selectedShippingCountry?.id || ''}
                          onChange={e => handleShippingCountryChange(e.target.value)}>
                          <option value="">Select Country *</option>
                          {countries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* State */}
                    <div className="rs1-select2 bor8 bg0 m-b-20">
                      <select style={selectStyle}
                        value={selectedShippingState?.id || ''}
                        onChange={e => handleShippingStateChange(e.target.value)}
                        disabled={!selectedShippingCountry || shippingStates.length === 0}>
                        <option value="">
                          {shippingStates.length === 0 && selectedShippingCountry
                            ? 'No states available'
                            : 'Select State / Province'}
                        </option>
                        {shippingStates.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="bor8 m-b-20">
                      <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                        placeholder="Postal Code *" value={shippingAddress.postalCode} required
                        onChange={e => handleShippingAddressChange('postalCode', e.target.value)} />
                    </div>
                    <div className="bor8 m-b-20">
                      <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
                        placeholder="Phone Number *" value={shippingAddress.phoneNumber} required
                        onChange={e => handleShippingAddressChange('phoneNumber', e.target.value)} />
                    </div>
                  </div>

                  {/* Billing Address */}
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                      <h4 className="mtext-109 cl2" style={{ margin: 0 }}>Billing Address</h4>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', userSelect: 'none' }}>
                        <input type="checkbox" checked={useSameAddress}
                          onChange={e => setUseSameAddress(e.target.checked)}
                          style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }} />
                        <span className="stext-107 cl2">Same as shipping</span>
                      </label>
                    </div>

                    {!useSameAddress && (
                      <>
                        <div className="bor8 m-b-20">
                          <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                            placeholder="Full Name *" value={billingAddress.fullName} required
                            onChange={e => handleBillingAddressChange('fullName', e.target.value)} />
                        </div>
                        <div className="bor8 m-b-20">
                          <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                            placeholder="Address Line 1 *" value={billingAddress.addressLine1} required
                            onChange={e => handleBillingAddressChange('addressLine1', e.target.value)} />
                        </div>
                        <div className="bor8 m-b-20">
                          <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                            placeholder="Address Line 2" value={billingAddress.addressLine2}
                            onChange={e => handleBillingAddressChange('addressLine2', e.target.value)} />
                        </div>
                        <div className="bor8 m-b-20">
                          <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                            placeholder="City *" value={billingAddress.city} required
                            onChange={e => handleBillingAddressChange('city', e.target.value)} />
                        </div>
                        <div className="rs1-select2 bor8 bg0 m-b-20">
                          {loadingCountries ? (
                            <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
                          ) : (
                            <select style={selectStyle}
                              value={selectedBillingCountry?.id || ''}
                              onChange={e => handleBillingCountryChange(e.target.value)}>
                              <option value="">Select Country *</option>
                              {countries.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="rs1-select2 bor8 bg0 m-b-20">
                          <select style={selectStyle}
                            value={selectedBillingState?.id || ''}
                            onChange={e => handleBillingStateChange(e.target.value)}
                            disabled={!selectedBillingCountry || billingStates.length === 0}>
                            <option value="">
                              {billingStates.length === 0 && selectedBillingCountry
                                ? 'No states available'
                                : 'Select State / Province'}
                            </option>
                            {billingStates.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="bor8 m-b-20">
                          <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
                            placeholder="Postal Code *" value={billingAddress.postalCode} required
                            onChange={e => handleBillingAddressChange('postalCode', e.target.value)} />
                        </div>
                        <div className="bor8 m-b-20">
                          <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
                            placeholder="Phone Number *" value={billingAddress.phoneNumber} required
                            onChange={e => handleBillingAddressChange('phoneNumber', e.target.value)} />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Order Notes */}
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
                    <h4 className="mtext-109 cl2 p-b-30">Order Notes (Optional)</h4>
                    <textarea className="stext-111 cl2 plh3 bor8 p-lr-20 p-tb-15"
                      placeholder="Notes about your order, e.g. special notes for delivery."
                      value={orderNotes} onChange={e => setOrderNotes(e.target.value)}
                      style={{ width: '100%', minHeight: '120px', resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {/* ══════════════ RIGHT: Order Summary ══════════════ */}
              <div className="col-lg-5 col-xl-4 m-lr-auto m-b-50">
                <div className="bor10 p-lr-40 p-t-30 p-b-40 m-l-63 m-r-40 m-lr-0-xl p-lr-15-sm">
                  <h4 className="mtext-109 cl2 p-b-30">Your Order</h4>

                  {/* Column headers */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid #e6e6e6', marginBottom: '15px' }}>
                    <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Product</span>
                    <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Subtotal</span>
                  </div>

                  {/* Line items */}
                  {cartItems.map(item => {
                    const effectivePrice = calculateDiscountedPrice(item.price, item.discount);
                    const showDiscount = hasDiscount(item.discount);
                    const rowTotal = effectivePrice * item.quantity;

                    return (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ flex: 1, paddingRight: '10px' }}>
                          <div className="stext-107 cl2" style={{ fontSize: '14px', marginBottom: '4px' }}>
                            {item.productName}
                          </div>
                          {item.variantText && (
                            <div style={{ fontSize: '12px', color: '#888' }}>{item.variantText}</div>
                          )}
                          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                            Qty: {item.quantity}
                          </div>
                          {showDiscount && (
                            <div style={{ marginTop: '3px' }}>
                              <span style={{ fontSize: '11px', color: '#aaa', textDecoration: 'line-through', marginRight: '6px' }}>
                                {formatPrice(item.price)}
                              </span>
                              <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
                                -{item.discount}%
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{ fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap' }}>
                          {formatPrice(rowTotal)}
                        </div>
                      </div>
                    );
                  })}

                  {/* Subtotal */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Subtotal</span>
                    <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
                      {formatPrice(subtotal)}
                    </span>
                  </div>

                  {/* Discount */}
                  {discountAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ fontSize: '14px', color: '#e74c3c' }}>You saved</span>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#e74c3c' }}>
                        -{formatPrice(discountAmount)}
                      </span>
                    </div>
                  )}

                  {/* Tax */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span className="stext-107 cl2" style={{ fontSize: '14px' }}>
                      Tax{taxPercentage > 0 ? ` (${taxPercentage}%)` : ''}
                    </span>
                    <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
                      {formatPrice(taxAmount)}
                    </span>
                  </div>

                  {/* Shipping */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Shipping</span>
                    <div style={{ textAlign: 'right' }}>
                      {isFreeShipping ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                            {shippingCost > 0 && (
                              <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: '13px' }}>
                                {formatPrice(shippingCost)}
                              </span>
                            )}
                            <span style={{ color: '#28a745', fontWeight: '700', fontSize: '14px' }}>Free</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
                            🎉 Orders over {formatPrice(freeShippingLimit)}
                          </div>
                        </>
                      ) : shippingCost > 0 ? (
                        <>
                          <span className="cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
                            {formatPrice(shippingCost)}
                          </span>
                          {freeShippingLimit > 0 && (
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                              Add {formatPrice(Math.max(0, freeShippingLimit - subtotal))} more for free shipping
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="cl2" style={{ fontSize: '14px' }}>TBD</span>
                      )}
                    </div>
                  </div>

                  {/* Total */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0 25px 0', borderBottom: '2px solid #333' }}>
                    <span className="mtext-110 cl2" style={{ fontSize: '16px', fontWeight: '700' }}>Total</span>
                    <span className="mtext-110 cl2" style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
                      {formatPrice(totalAmount)}
                    </span>
                  </div>

                  {/* ── Payment Method ── */}
                  <div className="p-t-30 p-b-30">
                    <h5 className="stext-109 cl2 p-b-15" style={{ fontSize: '15px', fontWeight: '600' }}>Payment Method</h5>

                    {([
                      { value: 'credit_card', label: 'Credit / Debit Card', icon: 'zmdi-card', payhere: true },
                      // { value: 'paypal', label: 'PayPal', icon: 'zmdi-paypal', payhere: true },
                      { value: 'bank_transfer', label: 'Bank Transfer', icon: 'zmdi-balance', payhere: false },
                      { value: 'cash_on_delivery', label: 'Cash on Delivery', icon: 'zmdi-money', payhere: false },
                    ] as const).map(({ value, label, icon, payhere }, idx, arr) => {
                      const isSelected = paymentMethod === value;
                      const isBT = value === 'bank_transfer';

                      return (
                        <div key={value} style={{
                          border: isSelected ? '1.5px solid #717fe0' : '1px solid #e6e6e6',
                          borderRadius: '8px',
                          marginBottom: idx < arr.length - 1 ? '8px' : 0,
                          backgroundColor: isSelected ? '#f6f7ff' : 'transparent',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                        }}>
                          {/* Radio row */}
                          <label className="flex-m stext-107 cl2 pointer" style={{
                            fontSize: '14px', cursor: 'pointer', width: '100%',
                            padding: '12px 15px', display: 'flex', alignItems: 'center', margin: 0,
                          }}>
                            <input
                              type="radio" name="paymentMethod" value={value}
                              checked={isSelected}
                              onChange={e => setPaymentMethod(e.target.value)}
                              style={{ marginRight: '10px', accentColor: '#717fe0' }}
                            />
                            <span style={{ flexGrow: 1 }}>{label}</span>
                            {payhere && (
                              <span style={{
                                fontSize: '10px', color: '#717fe0', fontWeight: 600,
                                backgroundColor: '#eef0fd', padding: '2px 7px',
                                borderRadius: '10px', marginLeft: '6px', whiteSpace: 'nowrap',
                              }}>
                                PayHere
                              </span>
                            )}
                          </label>

                          {/* PayHere info */}
                          {payhere && isSelected && (
                            <div style={{ borderTop: '1px dashed #d0d4f5', padding: '14px 15px 16px', backgroundColor: '#fafbff' }}>
                              {renderPayhereInfo()}
                            </div>
                          )}

                          {/* Bank transfer proof upload */}
                          {isBT && isSelected && (
                            <div style={{ borderTop: '1px dashed #d0d4f5', padding: '14px 15px 16px', backgroundColor: '#fafbff' }}>
                              {renderInlineProofUpload()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Place Order button */}
                  <button
                    type="submit"
                    className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
                    disabled={isButtonDisabled}
                    style={{
                      opacity: isButtonDisabled ? 0.6 : 1,
                      width: '100%', fontSize: '14px', padding: '16px 15px',
                      fontWeight: '600',
                      cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {buttonLabel}
                  </button>

                  {/* Hint under button */}
                  {isBankTransfer && !pendingProofFile && (
                    <p style={{
                      marginTop: '10px', textAlign: 'center', fontSize: '12px',
                      color: '#888', fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.6',
                    }}>
                      <i className="zmdi zmdi-alert-circle" style={{ marginRight: '4px', color: '#e09c2b' }} />
                      A payment receipt is required for Bank Transfer orders.
                    </p>
                  )}

                  {isPayhereMethod && (
                    <p style={{
                      marginTop: '10px', textAlign: 'center', fontSize: '11px',
                      color: '#aaa', fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.6',
                    }}>
                      <i className="zmdi zmdi-lock" style={{ marginRight: '4px' }} />
                      256-bit SSL secured payment via PayHere
                    </p>
                  )}
                </div>
              </div>

            </div>
          </form>
        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        .rs1-select2 { position: relative; }
        .rs1-select2::after {
          content: '▼'; position: absolute; right: 15px; top: 50%;
          transform: translateY(-50%); font-size: 10px; color: #666; pointer-events: none;
        }
        .rs1-select2 select:disabled { opacity: 0.5; cursor: not-allowed; }
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Checkout;

// // ---new---

// import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import Header from '../Header/Header';
// import Footer from '../Footer/Footer';
// import BackToTop from '../Back-to-top/BackToTop';
// import {
//   getCart,
//   clearCart,
//   getCartTotal,
//   CartItem,
// } from '../../Services/cartService';
// import { calculateDiscountedPrice, hasDiscount } from '../../Services/productService';
// import { useCompany } from '../../Contexts/CompanyContext';
// import { CheckoutService } from '../../Services/checkoutService';
// import {
//   getCountries,
//   getStatesByCountry,
//   getShippingInfo,
//   clearShippingInfo,
//   qualifiesForFreeShipping,
//   Country,
//   StateProvince,
// } from '../../Services/shippingService';
// import HeaderSpacer from '../Header/HeaderSpacer';

// interface ShippingAddress {
//   fullName: string;
//   addressLine1: string;
//   addressLine2: string;
//   city: string;
//   state: string;
//   postalCode: string;
//   country: string;
//   phoneNumber: string;
// }

// // ── Payment proof types ───────────────────────────────────────────────────────
// const PAYMENT_PROOF_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
// const PAYMENT_PROOF_MAX_MB = 10;

// const Checkout: React.FC = () => {
//   const navigate = useNavigate();
//   const [cartItems, setCartItems] = useState<CartItem[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState(false);

//   const { formatPrice, calculateTax, taxPercentage } = useCompany();

//   // ── Country / state data ──────────────────────────────────────────────────
//   const [countries, setCountries] = useState<Country[]>([]);
//   const [loadingCountries, setLoadingCountries] = useState(true);
//   const [shippingStates, setShippingStates] = useState<StateProvince[]>([]);
//   const [billingStates, setBillingStates] = useState<StateProvince[]>([]);

//   const [selectedShippingCountry, setSelectedShippingCountry] = useState<Country | null>(null);
//   const [selectedShippingState, setSelectedShippingState] = useState<StateProvince | null>(null);
//   const [selectedBillingCountry, setSelectedBillingCountry] = useState<Country | null>(null);
//   const [selectedBillingState, setSelectedBillingState] = useState<StateProvince | null>(null);

//   // ── Customer fields ───────────────────────────────────────────────────────
//   const [customerName, setCustomerName] = useState('');
//   const [customerEmail, setCustomerEmail] = useState('');
//   const [customerPhone, setCustomerPhone] = useState('');
//   const [customerWhatsApp, setCustomerWhatsApp] = useState('');

//   const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
//     fullName: '', addressLine1: '', addressLine2: '',
//     city: '', state: '', postalCode: '', country: '', phoneNumber: '',
//   });
//   const [billingAddress, setBillingAddress] = useState<ShippingAddress>({
//     fullName: '', addressLine1: '', addressLine2: '',
//     city: '', state: '', postalCode: '', country: '', phoneNumber: '',
//   });

//   const [useSameAddress, setUseSameAddress] = useState(true);
//   const [paymentMethod, setPaymentMethod] = useState('credit_card');
//   const [orderNotes, setOrderNotes] = useState('');

//   // ── Shipping figures ──────────────────────────────────────────────────────
//   const [shippingCost, setShippingCost] = useState(0);
//   const [freeShippingLimit, setFreeShippingLimit] = useState(0);

//   // ── Payment proof state ───────────────────────────────────────────────────
//   const [pendingProofFile, setPendingProofFile] = useState<File | null>(null);
//   const [pendingProofPreview, setPendingProofPreview] = useState<string | null>(null);
//   const [proofFileError, setProofFileError] = useState('');
//   const [isDragOver, setIsDragOver] = useState(false);
//   const proofInputRef = useRef<HTMLInputElement>(null);

//   // Derived
//   const isBankTransfer = paymentMethod === 'bank_transfer';

//   // ── Clean up object URL on unmount ────────────────────────────────────────
//   useEffect(() => {
//     return () => {
//       if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);
//     };
//   }, [pendingProofPreview]);

//   // ── When payment method changes away from bank_transfer, clear proof ──────
//   useEffect(() => {
//     if (!isBankTransfer) {
//       setPendingProofFile(null);
//       setProofFileError('');
//       setIsDragOver(false);
//       if (pendingProofPreview) {
//         URL.revokeObjectURL(pendingProofPreview);
//         setPendingProofPreview(null);
//       }
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isBankTransfer]);

//   // ── Load countries then patch saved shipping info ─────────────────────────
//   useEffect(() => {
//     (async () => {
//       try {
//         setLoadingCountries(true);
//         const loadedCountries = await getCountries();
//         setCountries(loadedCountries);

//         const saved = getShippingInfo();
//         if (!saved) return;

//         setShippingCost(saved.shippingCost);
//         setFreeShippingLimit(saved.freeShippingLimit ?? 0);

//         if (!saved.countryId) return;

//         const countryObj = loadedCountries.find(c => c.id === saved.countryId) ?? null;

//         if (countryObj) {
//           const countryStates = getStatesByCountry(countryObj.id, loadedCountries);
//           setShippingStates(countryStates);
//           setSelectedShippingCountry(countryObj);

//           if (saved.stateId) {
//             const stateObj = countryStates.find(s => s.id === saved.stateId) ?? null;
//             if (stateObj) {
//               setSelectedShippingState(stateObj);
//               setShippingAddress(prev => ({
//                 ...prev,
//                 country: countryObj.name,
//                 state: stateObj.name,
//                 postalCode: saved.postalCode ?? '',
//               }));
//             } else {
//               setShippingAddress(prev => ({
//                 ...prev,
//                 country: countryObj.name,
//                 state: saved.stateName ?? '',
//                 postalCode: saved.postalCode ?? '',
//               }));
//             }
//           } else {
//             setShippingAddress(prev => ({
//               ...prev,
//               country: countryObj.name,
//               postalCode: saved.postalCode ?? '',
//             }));
//           }
//         } else {
//           setShippingAddress(prev => ({
//             ...prev,
//             country: saved.countryName ?? '',
//             state: saved.stateName ?? '',
//             postalCode: saved.postalCode ?? '',
//           }));
//         }
//       } catch (err) {
//         console.error('Error loading countries:', err);
//       } finally {
//         setLoadingCountries(false);
//       }
//     })();
//   }, []);

//   // ── Shipping states update when country changes ───────────────────────────
//   const [countriesLoaded, setCountriesLoaded] = useState(false);
//   useEffect(() => {
//     if (!countriesLoaded) {
//       if (countries.length > 0) setCountriesLoaded(true);
//       return;
//     }
//     if (selectedShippingCountry) {
//       const newStates = getStatesByCountry(selectedShippingCountry.id, countries);
//       setShippingStates(newStates);
//       setSelectedShippingState(prev => {
//         if (prev && newStates.find(s => s.id === prev.id)) return prev;
//         setShippingAddress(p => ({ ...p, state: '' }));
//         return null;
//       });
//     } else {
//       setShippingStates([]);
//       setSelectedShippingState(null);
//       setShippingAddress(prev => ({ ...prev, state: '' }));
//     }
//   }, [selectedShippingCountry, countries, countriesLoaded]);

//   // ── Billing states update when country changes ────────────────────────────
//   useEffect(() => {
//     if (selectedBillingCountry) {
//       setBillingStates(getStatesByCountry(selectedBillingCountry.id, countries));
//       setSelectedBillingState(null);
//       setBillingAddress(prev => ({ ...prev, state: '' }));
//     } else {
//       setBillingStates([]);
//       setSelectedBillingState(null);
//       setBillingAddress(prev => ({ ...prev, state: '' }));
//     }
//   }, [selectedBillingCountry, countries]);

//   // ── Load cart ─────────────────────────────────────────────────────────────
//   useEffect(() => {
//     const items = getCart();
//     if (items.length === 0) { navigate('/cart'); return; }
//     setCartItems(items);
//   }, [navigate]);

//   // ── Sync billing address when "same as shipping" ──────────────────────────
//   useEffect(() => {
//     if (useSameAddress) {
//       setBillingAddress(shippingAddress);
//       setSelectedBillingCountry(selectedShippingCountry);
//       setSelectedBillingState(selectedShippingState);
//       setBillingStates(shippingStates);
//     }
//   }, [useSameAddress, shippingAddress, selectedShippingCountry, selectedShippingState, shippingStates]);

//   // ── Country / state change handlers ──────────────────────────────────────
//   const handleShippingCountryChange = (countryId: string) => {
//     const country = countries.find(c => c.id === countryId) ?? null;
//     setSelectedShippingCountry(country);
//     setShippingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
//   };

//   const handleShippingStateChange = (stateId: string) => {
//     const state = shippingStates.find(s => s.id === stateId) ?? null;
//     setSelectedShippingState(state);
//     setShippingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
//   };

//   const handleBillingCountryChange = (countryId: string) => {
//     const country = countries.find(c => c.id === countryId) ?? null;
//     setSelectedBillingCountry(country);
//     setBillingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
//   };

//   const handleBillingStateChange = (stateId: string) => {
//     const state = billingStates.find(s => s.id === stateId) ?? null;
//     setSelectedBillingState(state);
//     setBillingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
//   };

//   const handleShippingAddressChange = (field: keyof ShippingAddress, value: string) => {
//     setShippingAddress(prev => ({ ...prev, [field]: value }));
//   };

//   const handleBillingAddressChange = (field: keyof ShippingAddress, value: string) => {
//     setBillingAddress(prev => ({ ...prev, [field]: value }));
//   };

//   // ── Payment proof handlers ────────────────────────────────────────────────
//   const processProofFile = (file: File) => {
//     setProofFileError('');

//     const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
//     if (!allowedTypes.includes(file.type)) {
//       setProofFileError('Only images (JPEG, PNG, WebP, GIF) and PDF files are allowed.');
//       return;
//     }

//     const maxBytes = PAYMENT_PROOF_MAX_MB * 1024 * 1024;
//     if (file.size > maxBytes) {
//       setProofFileError(`File size must be under ${PAYMENT_PROOF_MAX_MB} MB.`);
//       return;
//     }

//     if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);

//     setPendingProofFile(file);

//     if (file.type !== 'application/pdf') {
//       setPendingProofPreview(URL.createObjectURL(file));
//     } else {
//       setPendingProofPreview(null);
//     }

//     if (proofInputRef.current) proofInputRef.current.value = '';
//   };

//   const handleProofFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const file = e.target.files?.[0];
//     if (!file) return;
//     processProofFile(file);
//   };

//   const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
//     e.preventDefault();
//     setIsDragOver(false);
//     const file = e.dataTransfer.files?.[0];
//     if (file) processProofFile(file);
//   };

//   const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
//     e.preventDefault();
//     setIsDragOver(true);
//   };

//   const handleDragLeave = () => setIsDragOver(false);

//   const handleClearProof = () => {
//     setPendingProofFile(null);
//     setProofFileError('');
//     if (pendingProofPreview) {
//       URL.revokeObjectURL(pendingProofPreview);
//       setPendingProofPreview(null);
//     }
//   };

//   // ── Discount calculation ──────────────────────────────────────────────────
//   const computeCartDiscountAmount = (items: CartItem[]): number =>
//     items.reduce((total, item) => {
//       if (!hasDiscount(item.discount)) return total;
//       const discounted = calculateDiscountedPrice(item.price, item.discount);
//       return total + (item.price - discounted) * item.quantity;
//     }, 0);

//   // ── Order-summary figures ─────────────────────────────────────────────────
//   const subtotal = getCartTotal();
//   const discountAmount = computeCartDiscountAmount(cartItems);
//   const taxAmount = calculateTax(subtotal);
//   const isFreeShipping = qualifiesForFreeShipping(subtotal, freeShippingLimit);
//   const finalShipping = isFreeShipping ? 0 : shippingCost;
//   const totalAmount = subtotal + taxAmount + finalShipping;

//   // ── Form validation ───────────────────────────────────────────────────────
//   const validateForm = (): boolean => {
//     if (!customerName.trim()) { setError('Please enter your name'); return false; }
//     if (!customerEmail.trim()) { setError('Please enter your email'); return false; }
//     if (!customerPhone.trim()) { setError('Please enter your phone number'); return false; }
//     if (!shippingAddress.fullName.trim()) { setError('Please enter shipping address full name'); return false; }
//     if (!shippingAddress.addressLine1.trim()) { setError('Please enter shipping address'); return false; }
//     if (!shippingAddress.city.trim()) { setError('Please enter city'); return false; }
//     if (!shippingAddress.postalCode.trim()) { setError('Please enter postal code'); return false; }
//     if (!shippingAddress.phoneNumber.trim()) { setError('Please enter phone number for shipping'); return false; }

//     if (isBankTransfer && !pendingProofFile) {
//       setError('Please upload your bank transfer receipt to proceed.');
//       return false;
//     }

//     return true;
//   };

//   // ── Place order ───────────────────────────────────────────────────────────
//   const handlePlaceOrder = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     setLoading(true);
//     setError('');

//     try {
//       const orderData = {
//         customerName,
//         customerEmail,
//         customerPhone,
//         customerWhatsApp,
//         items: cartItems.map(item => ({
//           productId: item.productId,
//           variantId: item.variantId || '',
//           quantity: item.quantity.toString(),
//         })),
//         subtotal: subtotal.toString(),
//         taxAmount: taxAmount.toString(),
//         shippingCost: finalShipping.toString(),
//         discountAmount: discountAmount.toString(),
//         status: 'pending' as const,
//         paymentStatus: 'pending' as const,
//         paymentMethod: paymentMethod as
//           'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery',
//         shippingAddress,
//         billingAddress: useSameAddress ? shippingAddress : billingAddress,
//         notes: orderNotes,
//         trackingNumber: '',
//         estimatedDelivery: '',
//         allocatedWarehouseId: '',
//       };

//       // ── Step 1: Create order via Cloud Function ───────────────────────────
//       const result = await CheckoutService.createOrder(orderData);

//       if (!result.success || !result.orderId) {
//         setError(result.message || 'Failed to place order');
//         return;
//       }

//       const orderId = result.orderId;

//       // ── Step 2: Upload proof then save metadata via Cloud Function ────────
//       if (isBankTransfer && pendingProofFile) {
//         try {
//           // Direct Storage upload (efficient for large files)
//           const proofResult = await CheckoutService.uploadPaymentProof(
//             pendingProofFile,
//             orderId
//           );
//           // Persist metadata to Firestore via Cloud Function
//           await CheckoutService.savePaymentProofToOrder(orderId, proofResult);
//         } catch (uploadErr) {
//           // Order is already created — log but don't block confirmation
//           console.error('Payment proof upload failed:', uploadErr);
//         }
//       }

//       // ── Step 3: Finalise ──────────────────────────────────────────────────
//       setSuccess(true);
//       clearCart();
//       clearShippingInfo();
//       setTimeout(() => navigate(`/order-confirmation/${orderId}`), 2000);

//     } catch (err) {
//       console.error('Checkout error:', err);
//       setError(err instanceof Error ? err.message : 'Failed to place order. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Shared select style ───────────────────────────────────────────────────
//   const selectStyle: React.CSSProperties = {
//     width: '100%', padding: '10px 15px', border: 'none',
//     background: 'transparent', cursor: 'pointer', fontSize: '13px',
//     color: '#555', appearance: 'none',
//     fontFamily: 'Poppins-Regular, sans-serif', outline: 'none',
//   };

//   // ── Inline payment proof upload (shown inside the Bank Transfer card) ─────
//   const renderInlineProofUpload = () => (
//     <div style={{ marginTop: '12px' }}>
//       <input
//         ref={proofInputRef}
//         type="file"
//         accept={PAYMENT_PROOF_ACCEPT}
//         style={{ display: 'none' }}
//         onChange={handleProofFileSelect}
//       />

//       {/* Error */}
//       {proofFileError && (
//         <div style={{
//           marginBottom: '10px',
//           padding: '8px 12px',
//           backgroundColor: '#fff5f5',
//           border: '1px solid #f5c6cb',
//           borderRadius: '6px',
//           color: '#721c24',
//           fontSize: '12px',
//           fontFamily: 'Poppins-Regular, sans-serif',
//           display: 'flex',
//           alignItems: 'center',
//           gap: '6px',
//         }}>
//           <i className="zmdi zmdi-alert-circle" style={{ fontSize: '14px', flexShrink: 0 }} />
//           {proofFileError}
//         </div>
//       )}

//       {pendingProofFile ? (
//         /* ── Attached file card ── */
//         <div style={{
//           border: '1px solid #b8f0c8',
//           borderRadius: '8px',
//           backgroundColor: '#f0fbf4',
//           padding: '12px',
//         }}>
//           {/* Top row: thumbnail + file info */}
//           <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
//             {/* Thumbnail / PDF icon */}
//             {pendingProofFile.type !== 'application/pdf' && pendingProofPreview ? (
//               <div style={{
//                 width: '42px', height: '42px', borderRadius: '6px',
//                 overflow: 'hidden', border: '1px solid #c3e6cb', flexShrink: 0,
//               }}>
//                 <img
//                   src={pendingProofPreview}
//                   alt="Receipt preview"
//                   style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
//                 />
//               </div>
//             ) : (
//               <div style={{
//                 width: '42px', height: '42px', borderRadius: '6px',
//                 backgroundColor: '#fff0f0', border: '1px solid #f5c6cb',
//                 display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
//               }}>
//                 <i className="zmdi zmdi-file-text" style={{ fontSize: '22px', color: '#c62828' }} />
//               </div>
//             )}

//             {/* File info — takes all remaining width */}
//             <div style={{ flex: 1, minWidth: 0 }}>
//               <p style={{
//                 margin: '0 0 3px 0', fontSize: '12px', fontWeight: 600,
//                 color: '#2d6a4f', overflow: 'hidden', textOverflow: 'ellipsis',
//                 whiteSpace: 'nowrap', fontFamily: 'Poppins-Regular, sans-serif',
//               }}>
//                 {pendingProofFile.name}
//               </p>
//               <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
//                 <i className="zmdi zmdi-check-circle" style={{ fontSize: '12px', color: '#28a745' }} />
//                 <span style={{ fontSize: '11px', color: '#28a745', fontFamily: 'Poppins-Regular, sans-serif' }}>
//                   Ready · {(pendingProofFile.size / 1024 / 1024).toFixed(2)} MB
//                 </span>
//               </div>
//             </div>
//           </div>

//           {/* Bottom row: action buttons */}
//           <div style={{
//             borderTop: '1px solid #c3e6cb',
//             paddingTop: '8px',
//             display: 'flex',
//             gap: '8px',
//           }}>
//             <button
//               type="button"
//               onClick={() => proofInputRef.current?.click()}
//               style={{
//                 flex: 1,
//                 background: '#fff',
//                 border: '1px solid #aed6bc',
//                 borderRadius: '6px',
//                 padding: '6px 0',
//                 cursor: 'pointer',
//                 fontSize: '12px',
//                 color: '#2d6a4f',
//                 fontFamily: 'Poppins-Regular, sans-serif',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 gap: '4px',
//                 transition: 'background 0.15s',
//               }}
//               onMouseEnter={e => (e.currentTarget.style.background = '#e8f5ee')}
//               onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
//             >
//               <i className="zmdi zmdi-refresh" style={{ fontSize: '13px' }} />
//               Change
//             </button>
//             <button
//               type="button"
//               onClick={handleClearProof}
//               style={{
//                 flex: 1,
//                 background: '#fff',
//                 border: '1px solid #e0e0e0',
//                 borderRadius: '6px',
//                 padding: '6px 0',
//                 cursor: 'pointer',
//                 fontSize: '12px',
//                 color: '#999',
//                 fontFamily: 'Poppins-Regular, sans-serif',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 gap: '4px',
//                 transition: 'all 0.15s',
//               }}
//               onMouseEnter={e => {
//                 e.currentTarget.style.background = '#fff5f5';
//                 e.currentTarget.style.borderColor = '#f5c6cb';
//                 e.currentTarget.style.color = '#c0392b';
//               }}
//               onMouseLeave={e => {
//                 e.currentTarget.style.background = '#fff';
//                 e.currentTarget.style.borderColor = '#e0e0e0';
//                 e.currentTarget.style.color = '#999';
//               }}
//             >
//               <i className="zmdi zmdi-delete" style={{ fontSize: '13px' }} />
//               Remove
//             </button>
//           </div>
//         </div>
//       ) : (
//         /* ── Drop zone ── */
//         <div
//           onClick={() => proofInputRef.current?.click()}
//           onDrop={handleDrop}
//           onDragOver={handleDragOver}
//           onDragLeave={handleDragLeave}
//           style={{
//             border: isDragOver ? '2px dashed #717fe0' : '2px dashed #c5caef',
//             borderRadius: '8px',
//             backgroundColor: isDragOver ? '#eef0fd' : '#fafbff',
//             padding: '16px 12px',
//             textAlign: 'center',
//             cursor: 'pointer',
//             transition: 'all 0.2s ease',
//           }}
//         >
//           <div style={{
//             display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
//             width: '36px', height: '36px', borderRadius: '50%',
//             backgroundColor: isDragOver ? '#717fe0' : '#eef0fd',
//             marginBottom: '8px', transition: 'all 0.2s',
//           }}>
//             <i
//               className="zmdi zmdi-cloud-upload"
//               style={{ fontSize: '18px', color: isDragOver ? '#fff' : '#717fe0' }}
//             />
//           </div>
//           <p style={{
//             margin: '0 0 3px 0', fontSize: '12px', fontWeight: 600,
//             color: '#444', fontFamily: 'Poppins-Regular, sans-serif',
//           }}>
//             Drop your receipt here or{' '}
//             <span style={{ color: '#717fe0', textDecoration: 'underline' }}>browse</span>
//           </p>
//           <p style={{
//             margin: 0, fontSize: '11px', color: '#999',
//             fontFamily: 'Poppins-Regular, sans-serif',
//           }}>
//             JPEG · PNG · WebP · GIF · PDF — max {PAYMENT_PROOF_MAX_MB} MB
//           </p>
//         </div>
//       )}

//       {/* Info note */}
//       <div style={{
//         marginTop: '10px',
//         padding: '8px 12px',
//         backgroundColor: '#f0f4ff',
//         borderLeft: '3px solid #717fe0',
//         borderRadius: '0 4px 4px 0',
//         display: 'flex',
//         alignItems: 'flex-start',
//         gap: '6px',
//       }}>
//         <i className="zmdi zmdi-info-outline" style={{ fontSize: '13px', color: '#717fe0', marginTop: '1px', flexShrink: 0 }} />
//         <p style={{
//           margin: 0, fontSize: '11px', color: '#666',
//           fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.6',
//         }}>
//           Orders are processed after payment verification, typically within 1–2 business days.
//         </p>
//       </div>
//     </div>
//   );

//   // ── Success screen ────────────────────────────────────────────────────────
//   if (success) {
//     return (
//       <div>
//         <Header />
//         <HeaderSpacer />
//         <div className="container" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//           <div className="text-center">
//             <i className="zmdi zmdi-check-circle" style={{ fontSize: '80px', color: '#28a745', marginBottom: '20px' }} />
//             <h2 className="mtext-111 cl2 p-b-16">Order Placed Successfully!</h2>
//             <p className="stext-113 cl6 p-b-26">Thank you for your order. You will receive a confirmation email shortly.</p>
//             <p className="stext-113 cl6">Redirecting to order confirmation...</p>
//           </div>
//         </div>
//         <Footer />
//       </div>
//     );
//   }

//   return (
//     <div>
//       <Header />
//       <HeaderSpacer />

//       {/* Breadcrumb */}
//       <div className="container">
//         <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
//           <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
//             Home <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
//           </a>
//           <a href="/cart" className="stext-109 cl8 hov-cl1 trans-04">
//             Shopping Cart <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
//           </a>
//           <span className="stext-109 cl4">Checkout</span>
//         </div>
//       </div>

//       <section className="bg0 p-t-75 p-b-120">
//         <div className="container">
//           {error && (
//             <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '5px', color: '#721c24' }}>
//               {error}
//             </div>
//           )}

//           <form onSubmit={handlePlaceOrder}>
//             <div className="row">

//               {/* ══════════════ LEFT ══════════════ */}
//               <div className="col-lg-7 col-xl-8 m-lr-auto m-b-50">
//                 <div className="m-l-25 m-r--38 m-lr-0-xl">

//                   {/* Customer Information */}
//                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
//                     <h4 className="mtext-109 cl2 p-b-30">Customer Information</h4>

//                     <div className="bor8 m-b-20 how-pos4-parent">
//                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="text"
//                         placeholder="Full Name *" value={customerName} required
//                         onChange={e => setCustomerName(e.target.value)} />
//                       <i className="zmdi zmdi-account how-pos4 pointer-none" />
//                     </div>
//                     <div className="bor8 m-b-20 how-pos4-parent">
//                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="email"
//                         placeholder="Email Address *" value={customerEmail} required
//                         onChange={e => setCustomerEmail(e.target.value)} />
//                       <i className="zmdi zmdi-email how-pos4 pointer-none" />
//                     </div>
//                     <div className="bor8 m-b-20 how-pos4-parent">
//                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
//                         placeholder="Phone Number *" value={customerPhone} required
//                         onChange={e => setCustomerPhone(e.target.value)} />
//                       <i className="zmdi zmdi-phone how-pos4 pointer-none" />
//                     </div>
//                     <div className="bor8 m-b-20 how-pos4-parent">
//                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
//                         placeholder="WhatsApp Number" value={customerWhatsApp}
//                         onChange={e => setCustomerWhatsApp(e.target.value)} />
//                       <i className="zmdi zmdi-whatsapp how-pos4 pointer-none" />
//                     </div>
//                   </div>

//                   {/* Shipping Address */}
//                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
//                     <h4 className="mtext-109 cl2 p-b-30">Shipping Address</h4>

//                     <div className="bor8 m-b-20">
//                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                         placeholder="Full Name *" value={shippingAddress.fullName} required
//                         onChange={e => handleShippingAddressChange('fullName', e.target.value)} />
//                     </div>
//                     <div className="bor8 m-b-20">
//                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                         placeholder="Address Line 1 *" value={shippingAddress.addressLine1} required
//                         onChange={e => handleShippingAddressChange('addressLine1', e.target.value)} />
//                     </div>
//                     <div className="bor8 m-b-20">
//                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                         placeholder="Address Line 2" value={shippingAddress.addressLine2}
//                         onChange={e => handleShippingAddressChange('addressLine2', e.target.value)} />
//                     </div>
//                     <div className="bor8 m-b-20">
//                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                         placeholder="City *" value={shippingAddress.city} required
//                         onChange={e => handleShippingAddressChange('city', e.target.value)} />
//                     </div>

//                     {/* Country */}
//                     <div className="rs1-select2 bor8 bg0 m-b-20">
//                       {loadingCountries ? (
//                         <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
//                       ) : (
//                         <select style={selectStyle}
//                           value={selectedShippingCountry?.id || ''}
//                           onChange={e => handleShippingCountryChange(e.target.value)}>
//                           <option value="">Select Country *</option>
//                           {countries.map(c => (
//                             <option key={c.id} value={c.id}>{c.name}</option>
//                           ))}
//                         </select>
//                       )}
//                     </div>

//                     {/* State */}
//                     <div className="rs1-select2 bor8 bg0 m-b-20">
//                       <select style={selectStyle}
//                         value={selectedShippingState?.id || ''}
//                         onChange={e => handleShippingStateChange(e.target.value)}
//                         disabled={!selectedShippingCountry || shippingStates.length === 0}>
//                         <option value="">
//                           {shippingStates.length === 0 && selectedShippingCountry
//                             ? 'No states available'
//                             : 'Select State / Province'}
//                         </option>
//                         {shippingStates.map(s => (
//                           <option key={s.id} value={s.id}>{s.name}</option>
//                         ))}
//                       </select>
//                     </div>

//                     <div className="bor8 m-b-20">
//                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                         placeholder="Postal Code *" value={shippingAddress.postalCode} required
//                         onChange={e => handleShippingAddressChange('postalCode', e.target.value)} />
//                     </div>
//                     <div className="bor8 m-b-20">
//                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
//                         placeholder="Phone Number *" value={shippingAddress.phoneNumber} required
//                         onChange={e => handleShippingAddressChange('phoneNumber', e.target.value)} />
//                     </div>
//                   </div>

//                   {/* Billing Address */}
//                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
//                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
//                       <h4 className="mtext-109 cl2" style={{ margin: 0 }}>Billing Address</h4>
//                       <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', userSelect: 'none' }}>
//                         <input type="checkbox" checked={useSameAddress}
//                           onChange={e => setUseSameAddress(e.target.checked)}
//                           style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }} />
//                         <span className="stext-107 cl2">Same as shipping</span>
//                       </label>
//                     </div>

//                     {!useSameAddress && (
//                       <>
//                         <div className="bor8 m-b-20">
//                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                             placeholder="Full Name *" value={billingAddress.fullName} required
//                             onChange={e => handleBillingAddressChange('fullName', e.target.value)} />
//                         </div>
//                         <div className="bor8 m-b-20">
//                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                             placeholder="Address Line 1 *" value={billingAddress.addressLine1} required
//                             onChange={e => handleBillingAddressChange('addressLine1', e.target.value)} />
//                         </div>
//                         <div className="bor8 m-b-20">
//                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                             placeholder="Address Line 2" value={billingAddress.addressLine2}
//                             onChange={e => handleBillingAddressChange('addressLine2', e.target.value)} />
//                         </div>
//                         <div className="bor8 m-b-20">
//                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                             placeholder="City *" value={billingAddress.city} required
//                             onChange={e => handleBillingAddressChange('city', e.target.value)} />
//                         </div>

//                         {/* Billing Country */}
//                         <div className="rs1-select2 bor8 bg0 m-b-20">
//                           {loadingCountries ? (
//                             <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
//                           ) : (
//                             <select style={selectStyle}
//                               value={selectedBillingCountry?.id || ''}
//                               onChange={e => handleBillingCountryChange(e.target.value)}>
//                               <option value="">Select Country *</option>
//                               {countries.map(c => (
//                                 <option key={c.id} value={c.id}>{c.name}</option>
//                               ))}
//                             </select>
//                           )}
//                         </div>

//                         {/* Billing State */}
//                         <div className="rs1-select2 bor8 bg0 m-b-20">
//                           <select style={selectStyle}
//                             value={selectedBillingState?.id || ''}
//                             onChange={e => handleBillingStateChange(e.target.value)}
//                             disabled={!selectedBillingCountry || billingStates.length === 0}>
//                             <option value="">
//                               {billingStates.length === 0 && selectedBillingCountry
//                                 ? 'No states available'
//                                 : 'Select State / Province'}
//                             </option>
//                             {billingStates.map(s => (
//                               <option key={s.id} value={s.id}>{s.name}</option>
//                             ))}
//                           </select>
//                         </div>

//                         <div className="bor8 m-b-20">
//                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
//                             placeholder="Postal Code *" value={billingAddress.postalCode} required
//                             onChange={e => handleBillingAddressChange('postalCode', e.target.value)} />
//                         </div>
//                         <div className="bor8 m-b-20">
//                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
//                             placeholder="Phone Number *" value={billingAddress.phoneNumber} required
//                             onChange={e => handleBillingAddressChange('phoneNumber', e.target.value)} />
//                         </div>
//                       </>
//                     )}
//                   </div>

//                   {/* Order Notes */}
//                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
//                     <h4 className="mtext-109 cl2 p-b-30">Order Notes (Optional)</h4>
//                     <textarea className="stext-111 cl2 plh3 bor8 p-lr-20 p-tb-15"
//                       placeholder="Notes about your order, e.g. special notes for delivery."
//                       value={orderNotes} onChange={e => setOrderNotes(e.target.value)}
//                       style={{ width: '100%', minHeight: '120px', resize: 'vertical' }} />
//                   </div>
//                 </div>
//               </div>

//               {/* ══════════════ RIGHT: Order Summary ══════════════ */}
//               <div className="col-lg-5 col-xl-4 m-lr-auto m-b-50">
//                 <div className="bor10 p-lr-40 p-t-30 p-b-40 m-l-63 m-r-40 m-lr-0-xl p-lr-15-sm">
//                   <h4 className="mtext-109 cl2 p-b-30">Your Order</h4>

//                   {/* Column headers */}
//                   <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid #e6e6e6', marginBottom: '15px' }}>
//                     <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Product</span>
//                     <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Subtotal</span>
//                   </div>

//                   {/* Line items */}
//                   {cartItems.map(item => {
//                     const effectivePrice = calculateDiscountedPrice(item.price, item.discount);
//                     const showDiscount = hasDiscount(item.discount);
//                     const rowTotal = effectivePrice * item.quantity;

//                     return (
//                       <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
//                         <div style={{ flex: 1, paddingRight: '10px' }}>
//                           <div className="stext-107 cl2" style={{ fontSize: '14px', marginBottom: '4px' }}>
//                             {item.productName}
//                           </div>
//                           {item.variantText && (
//                             <div style={{ fontSize: '12px', color: '#888' }}>{item.variantText}</div>
//                           )}
//                           <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
//                             Qty: {item.quantity}
//                           </div>
//                           {showDiscount && (
//                             <div style={{ marginTop: '3px' }}>
//                               <span style={{ fontSize: '11px', color: '#aaa', textDecoration: 'line-through', marginRight: '6px' }}>
//                                 {formatPrice(item.price)}
//                               </span>
//                               <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
//                                 -{item.discount}%
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                         <div style={{ fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap' }}>
//                           {formatPrice(rowTotal)}
//                         </div>
//                       </div>
//                     );
//                   })}

//                   {/* Subtotal */}
//                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
//                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Subtotal</span>
//                     <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
//                       {formatPrice(subtotal)}
//                     </span>
//                   </div>

//                   {/* Discount */}
//                   {discountAmount > 0 && (
//                     <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
//                       <span style={{ fontSize: '14px', color: '#e74c3c' }}>You saved</span>
//                       <span style={{ fontSize: '14px', fontWeight: '500', color: '#e74c3c' }}>
//                         -{formatPrice(discountAmount)}
//                       </span>
//                     </div>
//                   )}

//                   {/* Tax */}
//                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
//                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>
//                       Tax{taxPercentage > 0 ? ` (${taxPercentage}%)` : ''}
//                     </span>
//                     <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
//                       {formatPrice(taxAmount)}
//                     </span>
//                   </div>

//                   {/* Shipping */}
//                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
//                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Shipping</span>
//                     <div style={{ textAlign: 'right' }}>
//                       {isFreeShipping ? (
//                         <>
//                           <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
//                             {shippingCost > 0 && (
//                               <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: '13px' }}>
//                                 {formatPrice(shippingCost)}
//                               </span>
//                             )}
//                             <span style={{ color: '#28a745', fontWeight: '700', fontSize: '14px' }}>Free</span>
//                           </div>
//                           <div style={{ fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
//                             🎉 Orders over {formatPrice(freeShippingLimit)}
//                           </div>
//                         </>
//                       ) : shippingCost > 0 ? (
//                         <>
//                           <span className="cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
//                             {formatPrice(shippingCost)}
//                           </span>
//                           {freeShippingLimit > 0 && (
//                             <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
//                               Add {formatPrice(Math.max(0, freeShippingLimit - subtotal))} more for free shipping
//                             </div>
//                           )}
//                         </>
//                       ) : (
//                         <span className="cl2" style={{ fontSize: '14px' }}>TBD</span>
//                       )}
//                     </div>
//                   </div>

//                   {/* Total */}
//                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0 25px 0', borderBottom: '2px solid #333' }}>
//                     <span className="mtext-110 cl2" style={{ fontSize: '16px', fontWeight: '700' }}>Total</span>
//                     <span className="mtext-110 cl2" style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
//                       {formatPrice(totalAmount)}
//                     </span>
//                   </div>

//                   {/* ── Payment Method ── */}
//                   <div className="p-t-30 p-b-30">
//                     <h5 className="stext-109 cl2 p-b-15" style={{ fontSize: '15px', fontWeight: '600' }}>Payment Method</h5>

//                     {([
//                       { value: 'credit_card', label: 'Credit Card', icon: 'zmdi-card' },
//                       { value: 'paypal', label: 'PayPal', icon: 'zmdi-paypal' },
//                       { value: 'bank_transfer', label: 'Bank Transfer', icon: 'zmdi-balance' },
//                       { value: 'cash_on_delivery', label: 'Cash on Delivery', icon: 'zmdi-money' },
//                     ] as const).map(({ value, label, icon }, idx, arr) => {
//                       const isSelected = paymentMethod === value;
//                       const isBT = value === 'bank_transfer';

//                       return (
//                         <div
//                           key={value}
//                           style={{
//                             border: isSelected ? '1.5px solid #717fe0' : '1px solid #e6e6e6',
//                             borderRadius: '8px',
//                             marginBottom: idx < arr.length - 1 ? '8px' : 0,
//                             backgroundColor: isSelected ? '#f6f7ff' : 'transparent',
//                             overflow: 'hidden',
//                             transition: 'all 0.2s ease',
//                           }}
//                         >
//                           {/* Radio row */}
//                           <label
//                             className="flex-m stext-107 cl2 pointer"
//                             style={{
//                               fontSize: '14px', cursor: 'pointer',
//                               width: '100%', padding: '12px 15px',
//                               display: 'flex', alignItems: 'center',
//                               margin: 0,
//                             }}
//                           >
//                             <input
//                               type="radio"
//                               name="paymentMethod"
//                               value={value}
//                               checked={isSelected}
//                               onChange={e => setPaymentMethod(e.target.value)}
//                               style={{ marginRight: '10px', accentColor: '#717fe0' }}
//                             />
//                             <span style={{ flexGrow: 1 }}>{label}</span>
//                           </label>

//                           {/* Inline upload — only when Bank Transfer is selected */}
//                           {isBT && isSelected && (
//                             <div style={{
//                               borderTop: '1px dashed #d0d4f5',
//                               padding: '14px 15px 16px',
//                               backgroundColor: '#fafbff',
//                             }}>
//                               {renderInlineProofUpload()}
//                             </div>
//                           )}
//                         </div>
//                       );
//                     })}
//                   </div>

//                   {/* Place Order button */}
//                   <button
//                     type="submit"
//                     className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
//                     disabled={loading || (isBankTransfer && !pendingProofFile)}
//                     style={{
//                       opacity: loading || (isBankTransfer && !pendingProofFile) ? 0.6 : 1,
//                       width: '100%',
//                       fontSize: '14px',
//                       padding: '16px 15px',
//                       fontWeight: '600',
//                       cursor: (isBankTransfer && !pendingProofFile) ? 'not-allowed' : 'pointer',
//                     }}
//                   >
//                     {loading
//                       ? 'Processing...'
//                       : (isBankTransfer && !pendingProofFile)
//                         ? 'Upload Receipt to Continue'
//                         : 'Place Order'}
//                   </button>

//                   {/* Hint under button */}
//                   {isBankTransfer && !pendingProofFile && (
//                     <p style={{
//                       marginTop: '10px',
//                       textAlign: 'center',
//                       fontSize: '12px',
//                       color: '#888',
//                       fontFamily: 'Poppins-Regular, sans-serif',
//                       lineHeight: '1.6',
//                     }}>
//                       <i className="zmdi zmdi-alert-circle" style={{ marginRight: '4px', color: '#e09c2b' }} />
//                       A payment receipt is required for Bank Transfer orders.
//                     </p>
//                   )}
//                 </div>
//               </div>

//             </div>
//           </form>
//         </div>
//       </section>

//       <Footer />
//       <BackToTop />

//       <style>{`
//         .rs1-select2 { position: relative; }
//         .rs1-select2::after {
//           content: '▼'; position: absolute; right: 15px; top: 50%;
//           transform: translateY(-50%); font-size: 10px; color: #666; pointer-events: none;
//         }
//         .rs1-select2 select:disabled { opacity: 0.5; cursor: not-allowed; }
//       `}</style>
//     </div>
//   );
// };

// export default Checkout;



// // ---new---


// // import React, { useState, useEffect, useRef } from 'react';
// // import { useNavigate } from 'react-router-dom';
// // import Header from '../Header/Header';
// // import Footer from '../Footer/Footer';
// // import BackToTop from '../Back-to-top/BackToTop';
// // import {
// //   getCart,
// //   clearCart,
// //   getCartTotal,
// //   CartItem,
// // } from '../../Services/cartService';
// // import { calculateDiscountedPrice, hasDiscount } from '../../Services/productService';
// // import { useCompany } from '../../Contexts/CompanyContext';
// // import { CheckoutService } from '../../Services/checkoutService';
// // import {
// //   getCountries,
// //   getStatesByCountry,
// //   getShippingInfo,
// //   clearShippingInfo,
// //   qualifiesForFreeShipping,
// //   Country,
// //   StateProvince,
// // } from '../../Services/shippingService';
// // import HeaderSpacer from '../Header/HeaderSpacer';

// // interface ShippingAddress {
// //   fullName: string;
// //   addressLine1: string;
// //   addressLine2: string;
// //   city: string;
// //   state: string;
// //   postalCode: string;
// //   country: string;
// //   phoneNumber: string;
// // }

// // // ── Payment proof types ───────────────────────────────────────────────────────
// // const PAYMENT_PROOF_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
// // const PAYMENT_PROOF_MAX_MB = 10;

// // const Checkout: React.FC = () => {
// //   const navigate = useNavigate();
// //   const [cartItems, setCartItems] = useState<CartItem[]>([]);
// //   const [loading, setLoading] = useState(false);
// //   const [error, setError] = useState('');
// //   const [success, setSuccess] = useState(false);

// //   const { formatPrice, calculateTax, taxPercentage } = useCompany();

// //   // ── Country / state data ──────────────────────────────────────────────────
// //   const [countries, setCountries] = useState<Country[]>([]);
// //   const [loadingCountries, setLoadingCountries] = useState(true);
// //   const [shippingStates, setShippingStates] = useState<StateProvince[]>([]);
// //   const [billingStates, setBillingStates] = useState<StateProvince[]>([]);

// //   const [selectedShippingCountry, setSelectedShippingCountry] = useState<Country | null>(null);
// //   const [selectedShippingState, setSelectedShippingState] = useState<StateProvince | null>(null);
// //   const [selectedBillingCountry, setSelectedBillingCountry] = useState<Country | null>(null);
// //   const [selectedBillingState, setSelectedBillingState] = useState<StateProvince | null>(null);

// //   // ── Customer fields ───────────────────────────────────────────────────────
// //   const [customerName, setCustomerName] = useState('');
// //   const [customerEmail, setCustomerEmail] = useState('');
// //   const [customerPhone, setCustomerPhone] = useState('');
// //   const [customerWhatsApp, setCustomerWhatsApp] = useState('');

// //   const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
// //     fullName: '', addressLine1: '', addressLine2: '',
// //     city: '', state: '', postalCode: '', country: '', phoneNumber: '',
// //   });
// //   const [billingAddress, setBillingAddress] = useState<ShippingAddress>({
// //     fullName: '', addressLine1: '', addressLine2: '',
// //     city: '', state: '', postalCode: '', country: '', phoneNumber: '',
// //   });

// //   const [useSameAddress, setUseSameAddress] = useState(true);
// //   const [paymentMethod, setPaymentMethod] = useState('credit_card');
// //   const [orderNotes, setOrderNotes] = useState('');

// //   // ── Shipping figures ──────────────────────────────────────────────────────
// //   const [shippingCost, setShippingCost] = useState(0);
// //   const [freeShippingLimit, setFreeShippingLimit] = useState(0);

// //   // ── Payment proof state ───────────────────────────────────────────────────
// //   const [pendingProofFile, setPendingProofFile] = useState<File | null>(null);
// //   const [pendingProofPreview, setPendingProofPreview] = useState<string | null>(null);
// //   const [proofFileError, setProofFileError] = useState('');
// //   const [isDragOver, setIsDragOver] = useState(false);
// //   const proofInputRef = useRef<HTMLInputElement>(null);

// //   // Derived
// //   const isBankTransfer = paymentMethod === 'bank_transfer';

// //   // ── Clean up object URL on unmount ────────────────────────────────────────
// //   useEffect(() => {
// //     return () => {
// //       if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);
// //     };
// //   }, [pendingProofPreview]);

// //   // ── When payment method changes away from bank_transfer, clear proof ──────
// //   useEffect(() => {
// //     if (!isBankTransfer) {
// //       setPendingProofFile(null);
// //       setProofFileError('');
// //       setIsDragOver(false);
// //       if (pendingProofPreview) {
// //         URL.revokeObjectURL(pendingProofPreview);
// //         setPendingProofPreview(null);
// //       }
// //     }
// //     // eslint-disable-next-line react-hooks/exhaustive-deps
// //   }, [isBankTransfer]);

// //   // ── Load countries then patch saved shipping info ─────────────────────────
// //   useEffect(() => {
// //     (async () => {
// //       try {
// //         setLoadingCountries(true);
// //         const loadedCountries = await getCountries();
// //         setCountries(loadedCountries);

// //         const saved = getShippingInfo();
// //         if (!saved) return;

// //         setShippingCost(saved.shippingCost);
// //         setFreeShippingLimit(saved.freeShippingLimit ?? 0);

// //         if (!saved.countryId) return;

// //         const countryObj = loadedCountries.find(c => c.id === saved.countryId) ?? null;

// //         if (countryObj) {
// //           const countryStates = getStatesByCountry(countryObj.id, loadedCountries);
// //           setShippingStates(countryStates);
// //           setSelectedShippingCountry(countryObj);

// //           if (saved.stateId) {
// //             const stateObj = countryStates.find(s => s.id === saved.stateId) ?? null;
// //             if (stateObj) {
// //               setSelectedShippingState(stateObj);
// //               setShippingAddress(prev => ({
// //                 ...prev,
// //                 country: countryObj.name,
// //                 state: stateObj.name,
// //                 postalCode: saved.postalCode ?? '',
// //               }));
// //             } else {
// //               setShippingAddress(prev => ({
// //                 ...prev,
// //                 country: countryObj.name,
// //                 state: saved.stateName ?? '',
// //                 postalCode: saved.postalCode ?? '',
// //               }));
// //             }
// //           } else {
// //             setShippingAddress(prev => ({
// //               ...prev,
// //               country: countryObj.name,
// //               postalCode: saved.postalCode ?? '',
// //             }));
// //           }
// //         } else {
// //           setShippingAddress(prev => ({
// //             ...prev,
// //             country: saved.countryName ?? '',
// //             state: saved.stateName ?? '',
// //             postalCode: saved.postalCode ?? '',
// //           }));
// //         }
// //       } catch (err) {
// //         console.error('Error loading countries:', err);
// //       } finally {
// //         setLoadingCountries(false);
// //       }
// //     })();
// //   }, []);

// //   // ── Shipping states update when country changes ───────────────────────────
// //   const [countriesLoaded, setCountriesLoaded] = useState(false);
// //   useEffect(() => {
// //     if (!countriesLoaded) {
// //       if (countries.length > 0) setCountriesLoaded(true);
// //       return;
// //     }
// //     if (selectedShippingCountry) {
// //       const newStates = getStatesByCountry(selectedShippingCountry.id, countries);
// //       setShippingStates(newStates);
// //       setSelectedShippingState(prev => {
// //         if (prev && newStates.find(s => s.id === prev.id)) return prev;
// //         setShippingAddress(p => ({ ...p, state: '' }));
// //         return null;
// //       });
// //     } else {
// //       setShippingStates([]);
// //       setSelectedShippingState(null);
// //       setShippingAddress(prev => ({ ...prev, state: '' }));
// //     }
// //   }, [selectedShippingCountry, countries, countriesLoaded]);

// //   // ── Billing states update when country changes ────────────────────────────
// //   useEffect(() => {
// //     if (selectedBillingCountry) {
// //       setBillingStates(getStatesByCountry(selectedBillingCountry.id, countries));
// //       setSelectedBillingState(null);
// //       setBillingAddress(prev => ({ ...prev, state: '' }));
// //     } else {
// //       setBillingStates([]);
// //       setSelectedBillingState(null);
// //       setBillingAddress(prev => ({ ...prev, state: '' }));
// //     }
// //   }, [selectedBillingCountry, countries]);

// //   // ── Load cart ─────────────────────────────────────────────────────────────
// //   useEffect(() => {
// //     const items = getCart();
// //     if (items.length === 0) { navigate('/cart'); return; }
// //     setCartItems(items);
// //   }, [navigate]);

// //   // ── Sync billing address when "same as shipping" ──────────────────────────
// //   useEffect(() => {
// //     if (useSameAddress) {
// //       setBillingAddress(shippingAddress);
// //       setSelectedBillingCountry(selectedShippingCountry);
// //       setSelectedBillingState(selectedShippingState);
// //       setBillingStates(shippingStates);
// //     }
// //   }, [useSameAddress, shippingAddress, selectedShippingCountry, selectedShippingState, shippingStates]);

// //   // ── Country / state change handlers ──────────────────────────────────────
// //   const handleShippingCountryChange = (countryId: string) => {
// //     const country = countries.find(c => c.id === countryId) ?? null;
// //     setSelectedShippingCountry(country);
// //     setShippingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
// //   };

// //   const handleShippingStateChange = (stateId: string) => {
// //     const state = shippingStates.find(s => s.id === stateId) ?? null;
// //     setSelectedShippingState(state);
// //     setShippingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
// //   };

// //   const handleBillingCountryChange = (countryId: string) => {
// //     const country = countries.find(c => c.id === countryId) ?? null;
// //     setSelectedBillingCountry(country);
// //     setBillingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
// //   };

// //   const handleBillingStateChange = (stateId: string) => {
// //     const state = billingStates.find(s => s.id === stateId) ?? null;
// //     setSelectedBillingState(state);
// //     setBillingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
// //   };

// //   const handleShippingAddressChange = (field: keyof ShippingAddress, value: string) => {
// //     setShippingAddress(prev => ({ ...prev, [field]: value }));
// //   };

// //   const handleBillingAddressChange = (field: keyof ShippingAddress, value: string) => {
// //     setBillingAddress(prev => ({ ...prev, [field]: value }));
// //   };

// //   // ── Payment proof handlers ────────────────────────────────────────────────
// //   const processProofFile = (file: File) => {
// //     setProofFileError('');

// //     const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
// //     if (!allowedTypes.includes(file.type)) {
// //       setProofFileError('Only images (JPEG, PNG, WebP, GIF) and PDF files are allowed.');
// //       return;
// //     }

// //     const maxBytes = PAYMENT_PROOF_MAX_MB * 1024 * 1024;
// //     if (file.size > maxBytes) {
// //       setProofFileError(`File size must be under ${PAYMENT_PROOF_MAX_MB} MB.`);
// //       return;
// //     }

// //     if (pendingProofPreview) URL.revokeObjectURL(pendingProofPreview);

// //     setPendingProofFile(file);

// //     if (file.type !== 'application/pdf') {
// //       setPendingProofPreview(URL.createObjectURL(file));
// //     } else {
// //       setPendingProofPreview(null);
// //     }

// //     if (proofInputRef.current) proofInputRef.current.value = '';
// //   };

// //   const handleProofFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
// //     const file = e.target.files?.[0];
// //     if (!file) return;
// //     processProofFile(file);
// //   };

// //   const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
// //     e.preventDefault();
// //     setIsDragOver(false);
// //     const file = e.dataTransfer.files?.[0];
// //     if (file) processProofFile(file);
// //   };

// //   const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
// //     e.preventDefault();
// //     setIsDragOver(true);
// //   };

// //   const handleDragLeave = () => setIsDragOver(false);

// //   const handleClearProof = () => {
// //     setPendingProofFile(null);
// //     setProofFileError('');
// //     if (pendingProofPreview) {
// //       URL.revokeObjectURL(pendingProofPreview);
// //       setPendingProofPreview(null);
// //     }
// //   };

// //   // ── Discount calculation ──────────────────────────────────────────────────
// //   const computeCartDiscountAmount = (items: CartItem[]): number =>
// //     items.reduce((total, item) => {
// //       if (!hasDiscount(item.discount)) return total;
// //       const discounted = calculateDiscountedPrice(item.price, item.discount);
// //       return total + (item.price - discounted) * item.quantity;
// //     }, 0);

// //   // ── Order-summary figures ─────────────────────────────────────────────────
// //   const subtotal = getCartTotal();
// //   const discountAmount = computeCartDiscountAmount(cartItems);
// //   const taxAmount = calculateTax(subtotal);
// //   const isFreeShipping = qualifiesForFreeShipping(subtotal, freeShippingLimit);
// //   const finalShipping = isFreeShipping ? 0 : shippingCost;
// //   const totalAmount = subtotal + taxAmount + finalShipping;

// //   // ── Form validation ───────────────────────────────────────────────────────
// //   const validateForm = (): boolean => {
// //     if (!customerName.trim()) { setError('Please enter your name'); return false; }
// //     if (!customerEmail.trim()) { setError('Please enter your email'); return false; }
// //     if (!customerPhone.trim()) { setError('Please enter your phone number'); return false; }
// //     if (!shippingAddress.fullName.trim()) { setError('Please enter shipping address full name'); return false; }
// //     if (!shippingAddress.addressLine1.trim()) { setError('Please enter shipping address'); return false; }
// //     if (!shippingAddress.city.trim()) { setError('Please enter city'); return false; }
// //     if (!shippingAddress.postalCode.trim()) { setError('Please enter postal code'); return false; }
// //     if (!shippingAddress.phoneNumber.trim()) { setError('Please enter phone number for shipping'); return false; }

// //     if (isBankTransfer && !pendingProofFile) {
// //       setError('Please upload your bank transfer receipt to proceed.');
// //       return false;
// //     }

// //     return true;
// //   };

// //   // ── Place order ───────────────────────────────────────────────────────────
// //   const handlePlaceOrder = async (e: React.FormEvent) => {
// //     e.preventDefault();
// //     if (!validateForm()) return;

// //     setLoading(true);
// //     setError('');

// //     try {
// //       const orderData = {
// //         customerName,
// //         customerEmail,
// //         customerPhone,
// //         customerWhatsApp,
// //         items: cartItems.map(item => ({
// //           productId: item.productId,
// //           variantId: item.variantId || '',
// //           quantity: item.quantity.toString(),
// //         })),
// //         subtotal: subtotal.toString(),
// //         taxAmount: taxAmount.toString(),
// //         shippingCost: finalShipping.toString(),
// //         discountAmount: discountAmount.toString(),
// //         status: 'pending' as const,
// //         paymentStatus: 'pending' as const,
// //         paymentMethod: paymentMethod as
// //           'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery',
// //         shippingAddress,
// //         billingAddress: useSameAddress ? shippingAddress : billingAddress,
// //         notes: orderNotes,
// //         trackingNumber: '',
// //         estimatedDelivery: '',
// //         allocatedWarehouseId: '',
// //       };

// //       const result = await CheckoutService.createOrder(orderData);

// //       if (result.success) {
// //         if (isBankTransfer && pendingProofFile && result.orderId) {
// //           try {
// //             const proofResult = await CheckoutService.uploadPaymentProof(
// //               pendingProofFile,
// //               result.orderId
// //             );
// //             await CheckoutService.savePaymentProofToOrder(result.orderId, proofResult);
// //           } catch (uploadErr) {
// //             console.error('Payment proof upload failed:', uploadErr);
// //           }
// //         }

// //         setSuccess(true);
// //         clearCart();
// //         clearShippingInfo();
// //         setTimeout(() => navigate(`/order-confirmation/${result.orderId}`), 2000);
// //       } else {
// //         setError(result.message || 'Failed to place order');
// //       }
// //     } catch (err) {
// //       console.error('Checkout error:', err);
// //       setError(err instanceof Error ? err.message : 'Failed to place order. Please try again.');
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   // ── Shared select style ───────────────────────────────────────────────────
// //   const selectStyle: React.CSSProperties = {
// //     width: '100%', padding: '10px 15px', border: 'none',
// //     background: 'transparent', cursor: 'pointer', fontSize: '13px',
// //     color: '#555', appearance: 'none',
// //     fontFamily: 'Poppins-Regular, sans-serif', outline: 'none',
// //   };

// //   // ── Inline payment proof upload (shown inside the Bank Transfer card) ─────
// //   const renderInlineProofUpload = () => (
// //     <div style={{ marginTop: '12px' }}>
// //       <input
// //         ref={proofInputRef}
// //         type="file"
// //         accept={PAYMENT_PROOF_ACCEPT}
// //         style={{ display: 'none' }}
// //         onChange={handleProofFileSelect}
// //       />

// //       {/* Error */}
// //       {proofFileError && (
// //         <div style={{
// //           marginBottom: '10px',
// //           padding: '8px 12px',
// //           backgroundColor: '#fff5f5',
// //           border: '1px solid #f5c6cb',
// //           borderRadius: '6px',
// //           color: '#721c24',
// //           fontSize: '12px',
// //           fontFamily: 'Poppins-Regular, sans-serif',
// //           display: 'flex',
// //           alignItems: 'center',
// //           gap: '6px',
// //         }}>
// //           <i className="zmdi zmdi-alert-circle" style={{ fontSize: '14px', flexShrink: 0 }} />
// //           {proofFileError}
// //         </div>
// //       )}

// //       {pendingProofFile ? (
// //         /* ── Attached file card ── */
// //         <div style={{
// //           border: '1px solid #b8f0c8',
// //           borderRadius: '8px',
// //           backgroundColor: '#f0fbf4',
// //           padding: '12px',
// //         }}>
// //           {/* Top row: thumbnail + file info */}
// //           <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
// //             {/* Thumbnail / PDF icon */}
// //             {pendingProofFile.type !== 'application/pdf' && pendingProofPreview ? (
// //               <div style={{
// //                 width: '42px', height: '42px', borderRadius: '6px',
// //                 overflow: 'hidden', border: '1px solid #c3e6cb', flexShrink: 0,
// //               }}>
// //                 <img
// //                   src={pendingProofPreview}
// //                   alt="Receipt preview"
// //                   style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
// //                 />
// //               </div>
// //             ) : (
// //               <div style={{
// //                 width: '42px', height: '42px', borderRadius: '6px',
// //                 backgroundColor: '#fff0f0', border: '1px solid #f5c6cb',
// //                 display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
// //               }}>
// //                 <i className="zmdi zmdi-file-text" style={{ fontSize: '22px', color: '#c62828' }} />
// //               </div>
// //             )}

// //             {/* File info — takes all remaining width */}
// //             <div style={{ flex: 1, minWidth: 0 }}>
// //               <p style={{
// //                 margin: '0 0 3px 0', fontSize: '12px', fontWeight: 600,
// //                 color: '#2d6a4f', overflow: 'hidden', textOverflow: 'ellipsis',
// //                 whiteSpace: 'nowrap', fontFamily: 'Poppins-Regular, sans-serif',
// //               }}>
// //                 {pendingProofFile.name}
// //               </p>
// //               <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
// //                 <i className="zmdi zmdi-check-circle" style={{ fontSize: '12px', color: '#28a745' }} />
// //                 <span style={{ fontSize: '11px', color: '#28a745', fontFamily: 'Poppins-Regular, sans-serif' }}>
// //                   Ready · {(pendingProofFile.size / 1024 / 1024).toFixed(2)} MB
// //                 </span>
// //               </div>
// //             </div>
// //           </div>

// //           {/* Bottom row: action buttons full width, separated by a divider */}
// //           <div style={{
// //             borderTop: '1px solid #c3e6cb',
// //             paddingTop: '8px',
// //             display: 'flex',
// //             gap: '8px',
// //           }}>
// //             <button
// //               type="button"
// //               onClick={() => proofInputRef.current?.click()}
// //               style={{
// //                 flex: 1,
// //                 background: '#fff',
// //                 border: '1px solid #aed6bc',
// //                 borderRadius: '6px',
// //                 padding: '6px 0',
// //                 cursor: 'pointer',
// //                 fontSize: '12px',
// //                 color: '#2d6a4f',
// //                 fontFamily: 'Poppins-Regular, sans-serif',
// //                 display: 'flex',
// //                 alignItems: 'center',
// //                 justifyContent: 'center',
// //                 gap: '4px',
// //                 transition: 'background 0.15s',
// //               }}
// //               onMouseEnter={e => (e.currentTarget.style.background = '#e8f5ee')}
// //               onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
// //             >
// //               <i className="zmdi zmdi-refresh" style={{ fontSize: '13px' }} />
// //               Change
// //             </button>
// //             <button
// //               type="button"
// //               onClick={handleClearProof}
// //               style={{
// //                 flex: 1,
// //                 background: '#fff',
// //                 border: '1px solid #e0e0e0',
// //                 borderRadius: '6px',
// //                 padding: '6px 0',
// //                 cursor: 'pointer',
// //                 fontSize: '12px',
// //                 color: '#999',
// //                 fontFamily: 'Poppins-Regular, sans-serif',
// //                 display: 'flex',
// //                 alignItems: 'center',
// //                 justifyContent: 'center',
// //                 gap: '4px',
// //                 transition: 'all 0.15s',
// //               }}
// //               onMouseEnter={e => {
// //                 e.currentTarget.style.background = '#fff5f5';
// //                 e.currentTarget.style.borderColor = '#f5c6cb';
// //                 e.currentTarget.style.color = '#c0392b';
// //               }}
// //               onMouseLeave={e => {
// //                 e.currentTarget.style.background = '#fff';
// //                 e.currentTarget.style.borderColor = '#e0e0e0';
// //                 e.currentTarget.style.color = '#999';
// //               }}
// //             >
// //               <i className="zmdi zmdi-delete" style={{ fontSize: '13px' }} />
// //               Remove
// //             </button>
// //           </div>
// //         </div>
// //       ) : (
// //         /* ── Drop zone ── */
// //         <div
// //           onClick={() => proofInputRef.current?.click()}
// //           onDrop={handleDrop}
// //           onDragOver={handleDragOver}
// //           onDragLeave={handleDragLeave}
// //           style={{
// //             border: isDragOver ? '2px dashed #717fe0' : '2px dashed #c5caef',
// //             borderRadius: '8px',
// //             backgroundColor: isDragOver ? '#eef0fd' : '#fafbff',
// //             padding: '16px 12px',
// //             textAlign: 'center',
// //             cursor: 'pointer',
// //             transition: 'all 0.2s ease',
// //           }}
// //         >
// //           <div style={{
// //             display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
// //             width: '36px', height: '36px', borderRadius: '50%',
// //             backgroundColor: isDragOver ? '#717fe0' : '#eef0fd',
// //             marginBottom: '8px', transition: 'all 0.2s',
// //           }}>
// //             <i
// //               className="zmdi zmdi-cloud-upload"
// //               style={{ fontSize: '18px', color: isDragOver ? '#fff' : '#717fe0' }}
// //             />
// //           </div>
// //           <p style={{
// //             margin: '0 0 3px 0', fontSize: '12px', fontWeight: 600,
// //             color: '#444', fontFamily: 'Poppins-Regular, sans-serif',
// //           }}>
// //             Drop your receipt here or{' '}
// //             <span style={{ color: '#717fe0', textDecoration: 'underline' }}>browse</span>
// //           </p>
// //           <p style={{
// //             margin: 0, fontSize: '11px', color: '#999',
// //             fontFamily: 'Poppins-Regular, sans-serif',
// //           }}>
// //             JPEG · PNG · WebP · GIF · PDF — max {PAYMENT_PROOF_MAX_MB} MB
// //           </p>
// //         </div>
// //       )}

// //       {/* Info note */}
// //       <div style={{
// //         marginTop: '10px',
// //         padding: '8px 12px',
// //         backgroundColor: '#f0f4ff',
// //         borderLeft: '3px solid #717fe0',
// //         borderRadius: '0 4px 4px 0',
// //         display: 'flex',
// //         alignItems: 'flex-start',
// //         gap: '6px',
// //       }}>
// //         <i className="zmdi zmdi-info-outline" style={{ fontSize: '13px', color: '#717fe0', marginTop: '1px', flexShrink: 0 }} />
// //         <p style={{
// //           margin: 0, fontSize: '11px', color: '#666',
// //           fontFamily: 'Poppins-Regular, sans-serif', lineHeight: '1.6',
// //         }}>
// //           Orders are processed after payment verification, typically within 1–2 business days.
// //         </p>
// //       </div>
// //     </div>
// //   );

// //   // ── Success screen ────────────────────────────────────────────────────────
// //   if (success) {
// //     return (
// //       <div>
// //         <Header />
// //         <HeaderSpacer />
// //         <div className="container" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
// //           <div className="text-center">
// //             <i className="zmdi zmdi-check-circle" style={{ fontSize: '80px', color: '#28a745', marginBottom: '20px' }} />
// //             <h2 className="mtext-111 cl2 p-b-16">Order Placed Successfully!</h2>
// //             <p className="stext-113 cl6 p-b-26">Thank you for your order. You will receive a confirmation email shortly.</p>
// //             <p className="stext-113 cl6">Redirecting to order confirmation...</p>
// //           </div>
// //         </div>
// //         <Footer />
// //       </div>
// //     );
// //   }

// //   return (
// //     <div>
// //       <Header />
// //       <HeaderSpacer />

// //       {/* Breadcrumb */}
// //       <div className="container">
// //         <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
// //           <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
// //             Home <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
// //           </a>
// //           <a href="/cart" className="stext-109 cl8 hov-cl1 trans-04">
// //             Shopping Cart <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
// //           </a>
// //           <span className="stext-109 cl4">Checkout</span>
// //         </div>
// //       </div>

// //       <section className="bg0 p-t-75 p-b-120">
// //         <div className="container">
// //           {error && (
// //             <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '5px', color: '#721c24' }}>
// //               {error}
// //             </div>
// //           )}

// //           <form onSubmit={handlePlaceOrder}>
// //             <div className="row">

// //               {/* ══════════════ LEFT ══════════════ */}
// //               <div className="col-lg-7 col-xl-8 m-lr-auto m-b-50">
// //                 <div className="m-l-25 m-r--38 m-lr-0-xl">

// //                   {/* Customer Information */}
// //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
// //                     <h4 className="mtext-109 cl2 p-b-30">Customer Information</h4>

// //                     <div className="bor8 m-b-20 how-pos4-parent">
// //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="text"
// //                         placeholder="Full Name *" value={customerName} required
// //                         onChange={e => setCustomerName(e.target.value)} />
// //                       <i className="zmdi zmdi-account how-pos4 pointer-none" />
// //                     </div>
// //                     <div className="bor8 m-b-20 how-pos4-parent">
// //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="email"
// //                         placeholder="Email Address *" value={customerEmail} required
// //                         onChange={e => setCustomerEmail(e.target.value)} />
// //                       <i className="zmdi zmdi-email how-pos4 pointer-none" />
// //                     </div>
// //                     <div className="bor8 m-b-20 how-pos4-parent">
// //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
// //                         placeholder="Phone Number *" value={customerPhone} required
// //                         onChange={e => setCustomerPhone(e.target.value)} />
// //                       <i className="zmdi zmdi-phone how-pos4 pointer-none" />
// //                     </div>
// //                     <div className="bor8 m-b-20 how-pos4-parent">
// //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
// //                         placeholder="WhatsApp Number" value={customerWhatsApp}
// //                         onChange={e => setCustomerWhatsApp(e.target.value)} />
// //                       <i className="zmdi zmdi-whatsapp how-pos4 pointer-none" />
// //                     </div>
// //                   </div>

// //                   {/* Shipping Address */}
// //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
// //                     <h4 className="mtext-109 cl2 p-b-30">Shipping Address</h4>

// //                     <div className="bor8 m-b-20">
// //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                         placeholder="Full Name *" value={shippingAddress.fullName} required
// //                         onChange={e => handleShippingAddressChange('fullName', e.target.value)} />
// //                     </div>
// //                     <div className="bor8 m-b-20">
// //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                         placeholder="Address Line 1 *" value={shippingAddress.addressLine1} required
// //                         onChange={e => handleShippingAddressChange('addressLine1', e.target.value)} />
// //                     </div>
// //                     <div className="bor8 m-b-20">
// //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                         placeholder="Address Line 2" value={shippingAddress.addressLine2}
// //                         onChange={e => handleShippingAddressChange('addressLine2', e.target.value)} />
// //                     </div>
// //                     <div className="bor8 m-b-20">
// //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                         placeholder="City *" value={shippingAddress.city} required
// //                         onChange={e => handleShippingAddressChange('city', e.target.value)} />
// //                     </div>

// //                     {/* Country */}
// //                     <div className="rs1-select2 bor8 bg0 m-b-20">
// //                       {loadingCountries ? (
// //                         <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
// //                       ) : (
// //                         <select style={selectStyle}
// //                           value={selectedShippingCountry?.id || ''}
// //                           onChange={e => handleShippingCountryChange(e.target.value)}>
// //                           <option value="">Select Country *</option>
// //                           {countries.map(c => (
// //                             <option key={c.id} value={c.id}>{c.name}</option>
// //                           ))}
// //                         </select>
// //                       )}
// //                     </div>

// //                     {/* State */}
// //                     <div className="rs1-select2 bor8 bg0 m-b-20">
// //                       <select style={selectStyle}
// //                         value={selectedShippingState?.id || ''}
// //                         onChange={e => handleShippingStateChange(e.target.value)}
// //                         disabled={!selectedShippingCountry || shippingStates.length === 0}>
// //                         <option value="">
// //                           {shippingStates.length === 0 && selectedShippingCountry
// //                             ? 'No states available'
// //                             : 'Select State / Province'}
// //                         </option>
// //                         {shippingStates.map(s => (
// //                           <option key={s.id} value={s.id}>{s.name}</option>
// //                         ))}
// //                       </select>
// //                     </div>

// //                     <div className="bor8 m-b-20">
// //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                         placeholder="Postal Code *" value={shippingAddress.postalCode} required
// //                         onChange={e => handleShippingAddressChange('postalCode', e.target.value)} />
// //                     </div>
// //                     <div className="bor8 m-b-20">
// //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
// //                         placeholder="Phone Number *" value={shippingAddress.phoneNumber} required
// //                         onChange={e => handleShippingAddressChange('phoneNumber', e.target.value)} />
// //                     </div>
// //                   </div>

// //                   {/* Billing Address */}
// //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
// //                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
// //                       <h4 className="mtext-109 cl2" style={{ margin: 0 }}>Billing Address</h4>
// //                       <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', userSelect: 'none' }}>
// //                         <input type="checkbox" checked={useSameAddress}
// //                           onChange={e => setUseSameAddress(e.target.checked)}
// //                           style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }} />
// //                         <span className="stext-107 cl2">Same as shipping</span>
// //                       </label>
// //                     </div>

// //                     {!useSameAddress && (
// //                       <>
// //                         <div className="bor8 m-b-20">
// //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                             placeholder="Full Name *" value={billingAddress.fullName} required
// //                             onChange={e => handleBillingAddressChange('fullName', e.target.value)} />
// //                         </div>
// //                         <div className="bor8 m-b-20">
// //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                             placeholder="Address Line 1 *" value={billingAddress.addressLine1} required
// //                             onChange={e => handleBillingAddressChange('addressLine1', e.target.value)} />
// //                         </div>
// //                         <div className="bor8 m-b-20">
// //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                             placeholder="Address Line 2" value={billingAddress.addressLine2}
// //                             onChange={e => handleBillingAddressChange('addressLine2', e.target.value)} />
// //                         </div>
// //                         <div className="bor8 m-b-20">
// //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                             placeholder="City *" value={billingAddress.city} required
// //                             onChange={e => handleBillingAddressChange('city', e.target.value)} />
// //                         </div>

// //                         {/* Billing Country */}
// //                         <div className="rs1-select2 bor8 bg0 m-b-20">
// //                           {loadingCountries ? (
// //                             <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
// //                           ) : (
// //                             <select style={selectStyle}
// //                               value={selectedBillingCountry?.id || ''}
// //                               onChange={e => handleBillingCountryChange(e.target.value)}>
// //                               <option value="">Select Country *</option>
// //                               {countries.map(c => (
// //                                 <option key={c.id} value={c.id}>{c.name}</option>
// //                               ))}
// //                             </select>
// //                           )}
// //                         </div>

// //                         {/* Billing State */}
// //                         <div className="rs1-select2 bor8 bg0 m-b-20">
// //                           <select style={selectStyle}
// //                             value={selectedBillingState?.id || ''}
// //                             onChange={e => handleBillingStateChange(e.target.value)}
// //                             disabled={!selectedBillingCountry || billingStates.length === 0}>
// //                             <option value="">
// //                               {billingStates.length === 0 && selectedBillingCountry
// //                                 ? 'No states available'
// //                                 : 'Select State / Province'}
// //                             </option>
// //                             {billingStates.map(s => (
// //                               <option key={s.id} value={s.id}>{s.name}</option>
// //                             ))}
// //                           </select>
// //                         </div>

// //                         <div className="bor8 m-b-20">
// //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// //                             placeholder="Postal Code *" value={billingAddress.postalCode} required
// //                             onChange={e => handleBillingAddressChange('postalCode', e.target.value)} />
// //                         </div>
// //                         <div className="bor8 m-b-20">
// //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
// //                             placeholder="Phone Number *" value={billingAddress.phoneNumber} required
// //                             onChange={e => handleBillingAddressChange('phoneNumber', e.target.value)} />
// //                         </div>
// //                       </>
// //                     )}
// //                   </div>

// //                   {/* Order Notes */}
// //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
// //                     <h4 className="mtext-109 cl2 p-b-30">Order Notes (Optional)</h4>
// //                     <textarea className="stext-111 cl2 plh3 bor8 p-lr-20 p-tb-15"
// //                       placeholder="Notes about your order, e.g. special notes for delivery."
// //                       value={orderNotes} onChange={e => setOrderNotes(e.target.value)}
// //                       style={{ width: '100%', minHeight: '120px', resize: 'vertical' }} />
// //                   </div>
// //                 </div>
// //               </div>

// //               {/* ══════════════ RIGHT: Order Summary ══════════════ */}
// //               <div className="col-lg-5 col-xl-4 m-lr-auto m-b-50">
// //                 <div className="bor10 p-lr-40 p-t-30 p-b-40 m-l-63 m-r-40 m-lr-0-xl p-lr-15-sm">
// //                   <h4 className="mtext-109 cl2 p-b-30">Your Order</h4>

// //                   {/* Column headers */}
// //                   <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid #e6e6e6', marginBottom: '15px' }}>
// //                     <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Product</span>
// //                     <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Subtotal</span>
// //                   </div>

// //                   {/* Line items */}
// //                   {cartItems.map(item => {
// //                     const effectivePrice = calculateDiscountedPrice(item.price, item.discount);
// //                     const showDiscount = hasDiscount(item.discount);
// //                     const rowTotal = effectivePrice * item.quantity;

// //                     return (
// //                       <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
// //                         <div style={{ flex: 1, paddingRight: '10px' }}>
// //                           <div className="stext-107 cl2" style={{ fontSize: '14px', marginBottom: '4px' }}>
// //                             {item.productName}
// //                           </div>
// //                           {item.variantText && (
// //                             <div style={{ fontSize: '12px', color: '#888' }}>{item.variantText}</div>
// //                           )}
// //                           <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
// //                             Qty: {item.quantity}
// //                           </div>
// //                           {showDiscount && (
// //                             <div style={{ marginTop: '3px' }}>
// //                               <span style={{ fontSize: '11px', color: '#aaa', textDecoration: 'line-through', marginRight: '6px' }}>
// //                                 {formatPrice(item.price)}
// //                               </span>
// //                               <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
// //                                 -{item.discount}%
// //                               </span>
// //                             </div>
// //                           )}
// //                         </div>
// //                         <div style={{ fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap' }}>
// //                           {formatPrice(rowTotal)}
// //                         </div>
// //                       </div>
// //                     );
// //                   })}

// //                   {/* Subtotal */}
// //                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// //                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Subtotal</span>
// //                     <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
// //                       {formatPrice(subtotal)}
// //                     </span>
// //                   </div>

// //                   {/* Discount */}
// //                   {discountAmount > 0 && (
// //                     <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// //                       <span style={{ fontSize: '14px', color: '#e74c3c' }}>You saved</span>
// //                       <span style={{ fontSize: '14px', fontWeight: '500', color: '#e74c3c' }}>
// //                         -{formatPrice(discountAmount)}
// //                       </span>
// //                     </div>
// //                   )}

// //                   {/* Tax */}
// //                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// //                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>
// //                       Tax{taxPercentage > 0 ? ` (${taxPercentage}%)` : ''}
// //                     </span>
// //                     <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
// //                       {formatPrice(taxAmount)}
// //                     </span>
// //                   </div>

// //                   {/* Shipping */}
// //                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// //                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Shipping</span>
// //                     <div style={{ textAlign: 'right' }}>
// //                       {isFreeShipping ? (
// //                         <>
// //                           <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
// //                             {shippingCost > 0 && (
// //                               <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: '13px' }}>
// //                                 {formatPrice(shippingCost)}
// //                               </span>
// //                             )}
// //                             <span style={{ color: '#28a745', fontWeight: '700', fontSize: '14px' }}>Free</span>
// //                           </div>
// //                           <div style={{ fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
// //                             🎉 Orders over {formatPrice(freeShippingLimit)}
// //                           </div>
// //                         </>
// //                       ) : shippingCost > 0 ? (
// //                         <>
// //                           <span className="cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
// //                             {formatPrice(shippingCost)}
// //                           </span>
// //                           {freeShippingLimit > 0 && (
// //                             <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
// //                               Add {formatPrice(Math.max(0, freeShippingLimit - subtotal))} more for free shipping
// //                             </div>
// //                           )}
// //                         </>
// //                       ) : (
// //                         <span className="cl2" style={{ fontSize: '14px' }}>TBD</span>
// //                       )}
// //                     </div>
// //                   </div>

// //                   {/* Total */}
// //                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0 25px 0', borderBottom: '2px solid #333' }}>
// //                     <span className="mtext-110 cl2" style={{ fontSize: '16px', fontWeight: '700' }}>Total</span>
// //                     <span className="mtext-110 cl2" style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
// //                       {formatPrice(totalAmount)}
// //                     </span>
// //                   </div>

// //                   {/* ── Payment Method ── */}
// //                   <div className="p-t-30 p-b-30">
// //                     <h5 className="stext-109 cl2 p-b-15" style={{ fontSize: '15px', fontWeight: '600' }}>Payment Method</h5>

// //                     {([
// //                       { value: 'credit_card', label: 'Credit Card', icon: 'zmdi-card' },
// //                       { value: 'paypal', label: 'PayPal', icon: 'zmdi-paypal' },
// //                       { value: 'bank_transfer', label: 'Bank Transfer', icon: 'zmdi-balance' },
// //                       { value: 'cash_on_delivery', label: 'Cash on Delivery', icon: 'zmdi-money' },
// //                     ] as const).map(({ value, label, icon }, idx, arr) => {
// //                       const isSelected = paymentMethod === value;
// //                       const isBT = value === 'bank_transfer';

// //                       return (
// //                         <div
// //                           key={value}
// //                           style={{
// //                             border: isSelected
// //                               ? (isBT ? '1.5px solid #717fe0' : '1.5px solid #717fe0')
// //                               : '1px solid #e6e6e6',
// //                             borderRadius: '8px',
// //                             marginBottom: idx < arr.length - 1 ? '8px' : 0,
// //                             backgroundColor: isSelected
// //                               ? (isBT ? '#f6f7ff' : '#f6f7ff')
// //                               : 'transparent',
// //                             overflow: 'hidden',
// //                             transition: 'all 0.2s ease',
// //                           }}
// //                         >
// //                           {/* Radio row */}
// //                           <label
// //                             className="flex-m stext-107 cl2 pointer"
// //                             style={{
// //                               fontSize: '14px', cursor: 'pointer',
// //                               width: '100%', padding: '12px 15px',
// //                               display: 'flex', alignItems: 'center',
// //                               margin: 0,
// //                             }}
// //                           >
// //                             <input
// //                               type="radio"
// //                               name="paymentMethod"
// //                               value={value}
// //                               checked={isSelected}
// //                               onChange={e => setPaymentMethod(e.target.value)}
// //                               style={{ marginRight: '10px', accentColor: '#717fe0' }}
// //                             />
// //                             <span style={{ flexGrow: 1 }}>{label}</span>
// //                           </label>

// //                           {/* ── Inline upload — only when Bank Transfer is selected ── */}
// //                           {isBT && isSelected && (
// //                             <div style={{
// //                               borderTop: '1px dashed #d0d4f5',
// //                               padding: '14px 15px 16px',
// //                               backgroundColor: '#fafbff',
// //                             }}>
// //                               {renderInlineProofUpload()}
// //                             </div>
// //                           )}
// //                         </div>
// //                       );
// //                     })}
// //                   </div>

// //                   {/* Place Order button */}
// //                   <button
// //                     type="submit"
// //                     className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
// //                     disabled={loading || (isBankTransfer && !pendingProofFile)}
// //                     style={{
// //                       opacity: loading || (isBankTransfer && !pendingProofFile) ? 0.6 : 1,
// //                       width: '100%',
// //                       fontSize: '14px',
// //                       padding: '16px 15px',
// //                       fontWeight: '600',
// //                       cursor: (isBankTransfer && !pendingProofFile) ? 'not-allowed' : 'pointer',
// //                     }}
// //                   >
// //                     {loading
// //                       ? 'Processing...'
// //                       : (isBankTransfer && !pendingProofFile)
// //                         ? 'Upload Receipt to Continue'
// //                         : 'Place Order'}
// //                   </button>

// //                   {/* Hint under button */}
// //                   {isBankTransfer && !pendingProofFile && (
// //                     <p style={{
// //                       marginTop: '10px',
// //                       textAlign: 'center',
// //                       fontSize: '12px',
// //                       color: '#888',
// //                       fontFamily: 'Poppins-Regular, sans-serif',
// //                       lineHeight: '1.6',
// //                     }}>
// //                       <i className="zmdi zmdi-alert-circle" style={{ marginRight: '4px', color: '#e09c2b' }} />
// //                       A payment receipt is required for Bank Transfer orders.
// //                     </p>
// //                   )}
// //                 </div>
// //               </div>

// //             </div>
// //           </form>
// //         </div>
// //       </section>

// //       <Footer />
// //       <BackToTop />

// //       <style>{`
// //         .rs1-select2 { position: relative; }
// //         .rs1-select2::after {
// //           content: '▼'; position: absolute; right: 15px; top: 50%;
// //           transform: translateY(-50%); font-size: 10px; color: #666; pointer-events: none;
// //         }
// //         .rs1-select2 select:disabled { opacity: 0.5; cursor: not-allowed; }
// //       `}</style>
// //     </div>
// //   );
// // };

// // export default Checkout;


// // // import React, { useState, useEffect } from 'react';
// // // import { useNavigate } from 'react-router-dom';
// // // import Header from '../Header/Header';
// // // import Footer from '../Footer/Footer';
// // // import BackToTop from '../Back-to-top/BackToTop';
// // // import {
// // //   getCart,
// // //   clearCart,
// // //   getCartTotal,
// // //   CartItem,
// // // } from '../../Services/cartService';
// // // import { calculateDiscountedPrice, hasDiscount } from '../../Services/productService';
// // // import { useCompany } from '../../Contexts/CompanyContext';
// // // import { CheckoutService } from '../../Services/checkoutService';
// // // import {
// // //   getCountries,
// // //   getStatesByCountry,
// // //   getShippingInfo,
// // //   clearShippingInfo,
// // //   qualifiesForFreeShipping,
// // //   Country,
// // //   StateProvince,
// // // } from '../../Services/shippingService';
// // // import HeaderSpacer from '../Header/HeaderSpacer';

// // // interface ShippingAddress {
// // //   fullName: string;
// // //   addressLine1: string;
// // //   addressLine2: string;
// // //   city: string;
// // //   state: string;
// // //   postalCode: string;
// // //   country: string;
// // //   phoneNumber: string;
// // // }

// // // const Checkout: React.FC = () => {
// // //   const navigate = useNavigate();
// // //   const [cartItems, setCartItems] = useState<CartItem[]>([]);
// // //   const [loading, setLoading] = useState(false);
// // //   const [error, setError] = useState('');
// // //   const [success, setSuccess] = useState(false);

// // //   const { formatPrice, calculateTax, taxPercentage } = useCompany();

// // //   // ── Country / state data ──────────────────────────────────────────────────
// // //   const [countries, setCountries] = useState<Country[]>([]);
// // //   const [loadingCountries, setLoadingCountries] = useState(true);
// // //   const [shippingStates, setShippingStates] = useState<StateProvince[]>([]);
// // //   const [billingStates, setBillingStates] = useState<StateProvince[]>([]);

// // //   // Track selected country/state objects so we can store NAMES in the address
// // //   const [selectedShippingCountry, setSelectedShippingCountry] = useState<Country | null>(null);
// // //   const [selectedShippingState,   setSelectedShippingState]   = useState<StateProvince | null>(null);
// // //   const [selectedBillingCountry,  setSelectedBillingCountry]  = useState<Country | null>(null);
// // //   const [selectedBillingState,    setSelectedBillingState]    = useState<StateProvince | null>(null);

// // //   // ── Customer fields ───────────────────────────────────────────────────────
// // //   const [customerName,  setCustomerName]  = useState('');
// // //   const [customerEmail, setCustomerEmail] = useState('');
// // //   const [customerPhone, setCustomerPhone] = useState('');

// // //   // Addresses store country/state as NAMES (not IDs) for admin-panel compatibility
// // //   const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
// // //     fullName: '', addressLine1: '', addressLine2: '',
// // //     city: '', state: '', postalCode: '', country: '', phoneNumber: '',
// // //   });
// // //   const [billingAddress, setBillingAddress] = useState<ShippingAddress>({
// // //     fullName: '', addressLine1: '', addressLine2: '',
// // //     city: '', state: '', postalCode: '', country: '', phoneNumber: '',
// // //   });

// // //   const [useSameAddress, setUseSameAddress] = useState(true);
// // //   const [paymentMethod,  setPaymentMethod]  = useState('credit_card');
// // //   const [orderNotes,     setOrderNotes]     = useState('');

// // //   // Shipping figures loaded from saved ShippingInfo (set in Cart)
// // //   // shippingCost = raw cost before free-shipping check
// // //   const [shippingCost,      setShippingCost]      = useState(0);
// // //   const [freeShippingLimit, setFreeShippingLimit] = useState(0);

// // //   // ── Load countries then patch saved shipping info ─────────────────────────
// // //   // We do both in one effect so we have the countries list available when we
// // //   // need to resolve countryId/stateId → Country/StateProvince objects.
// // //   useEffect(() => {
// // //     (async () => {
// // //       try {
// // //         setLoadingCountries(true);
// // //         const loadedCountries = await getCountries();
// // //         setCountries(loadedCountries);

// // //         // Read shipping info that Cart saved to sessionStorage
// // //         const saved = getShippingInfo();
// // //         if (!saved) return;

// // //         // Restore shipping cost and free-shipping threshold
// // //         setShippingCost(saved.shippingCost);
// // //         setFreeShippingLimit(saved.freeShippingLimit ?? 0);

// // //         if (!saved.countryId) return;

// // //         // Resolve Country object by ID
// // //         const countryObj = loadedCountries.find(c => c.id === saved.countryId) ?? null;

// // //         if (countryObj) {
// // //           // Resolve states for this country
// // //           const countryStates = getStatesByCountry(countryObj.id, loadedCountries);
// // //           setShippingStates(countryStates);
// // //           setSelectedShippingCountry(countryObj);

// // //           if (saved.stateId) {
// // //             // Resolve StateProvince object by ID
// // //             const stateObj = countryStates.find(s => s.id === saved.stateId) ?? null;
// // //             if (stateObj) {
// // //               setSelectedShippingState(stateObj);
// // //               setShippingAddress(prev => ({
// // //                 ...prev,
// // //                 country: countryObj.name,
// // //                 state: stateObj.name,
// // //                 postalCode: saved.postalCode ?? '',
// // //               }));
// // //             } else {
// // //               // State not in list — use saved name
// // //               setShippingAddress(prev => ({
// // //                 ...prev,
// // //                 country: countryObj.name,
// // //                 state: saved.stateName ?? '',
// // //                 postalCode: saved.postalCode ?? '',
// // //               }));
// // //             }
// // //           } else {
// // //             setShippingAddress(prev => ({
// // //               ...prev,
// // //               country: countryObj.name,
// // //               postalCode: saved.postalCode ?? '',
// // //             }));
// // //           }
// // //         } else {
// // //           // Country not in list — fall back to saved names
// // //           setShippingAddress(prev => ({
// // //             ...prev,
// // //             country: saved.countryName ?? '',
// // //             state: saved.stateName ?? '',
// // //             postalCode: saved.postalCode ?? '',
// // //           }));
// // //         }
// // //       } catch (err) {
// // //         console.error('Error loading countries:', err);
// // //       } finally {
// // //         setLoadingCountries(false);
// // //       }
// // //     })();
// // //   }, []);

// // //   // ── Shipping states update when country changes ───────────────────────────
// // //   // We use a ref guard so the initial pre-population (above) doesn't reset the
// // //   // pre-filled state. After the first mount the effect runs normally.
// // //   const [countriesLoaded, setCountriesLoaded] = useState(false);
// // //   useEffect(() => {
// // //     if (!countriesLoaded) {
// // //       // Skip on very first render — pre-population handles this
// // //       if (countries.length > 0) setCountriesLoaded(true);
// // //       return;
// // //     }
// // //     if (selectedShippingCountry) {
// // //       const newStates = getStatesByCountry(selectedShippingCountry.id, countries);
// // //       setShippingStates(newStates);
// // //       // Reset state selection only if it doesn't belong to the new country
// // //       setSelectedShippingState(prev => {
// // //         if (prev && newStates.find(s => s.id === prev.id)) return prev;
// // //         setShippingAddress(p => ({ ...p, state: '' }));
// // //         return null;
// // //       });
// // //     } else {
// // //       setShippingStates([]);
// // //       setSelectedShippingState(null);
// // //       setShippingAddress(prev => ({ ...prev, state: '' }));
// // //     }
// // //   }, [selectedShippingCountry, countries, countriesLoaded]);

// // //   // ── Billing states update when country changes ────────────────────────────
// // //   useEffect(() => {
// // //     if (selectedBillingCountry) {
// // //       setBillingStates(getStatesByCountry(selectedBillingCountry.id, countries));
// // //       setSelectedBillingState(null);
// // //       setBillingAddress(prev => ({ ...prev, state: '' }));
// // //     } else {
// // //       setBillingStates([]);
// // //       setSelectedBillingState(null);
// // //       setBillingAddress(prev => ({ ...prev, state: '' }));
// // //     }
// // //   }, [selectedBillingCountry, countries]);

// // //   // ── Load cart ─────────────────────────────────────────────────────────────
// // //   useEffect(() => {
// // //     const items = getCart();
// // //     if (items.length === 0) { navigate('/cart'); return; }
// // //     setCartItems(items);
// // //   }, [navigate]);

// // //   // ── Sync billing address when "same as shipping" ──────────────────────────
// // //   useEffect(() => {
// // //     if (useSameAddress) {
// // //       setBillingAddress(shippingAddress);
// // //       setSelectedBillingCountry(selectedShippingCountry);
// // //       setSelectedBillingState(selectedShippingState);
// // //       setBillingStates(shippingStates);
// // //     }
// // //   }, [useSameAddress, shippingAddress, selectedShippingCountry, selectedShippingState, shippingStates]);

// // //   // ── Country / state change handlers ──────────────────────────────────────
// // //   // Select widgets use IDs; we resolve & store NAMES in the address object so
// // //   // the admin panel can display them without any ID lookup.

// // //   const handleShippingCountryChange = (countryId: string) => {
// // //     const country = countries.find(c => c.id === countryId) ?? null;
// // //     setSelectedShippingCountry(country);
// // //     setShippingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
// // //   };

// // //   const handleShippingStateChange = (stateId: string) => {
// // //     const state = shippingStates.find(s => s.id === stateId) ?? null;
// // //     setSelectedShippingState(state);
// // //     setShippingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
// // //   };

// // //   const handleBillingCountryChange = (countryId: string) => {
// // //     const country = countries.find(c => c.id === countryId) ?? null;
// // //     setSelectedBillingCountry(country);
// // //     setBillingAddress(prev => ({ ...prev, country: country?.name ?? '', state: '' }));
// // //   };

// // //   const handleBillingStateChange = (stateId: string) => {
// // //     const state = billingStates.find(s => s.id === stateId) ?? null;
// // //     setSelectedBillingState(state);
// // //     setBillingAddress(prev => ({ ...prev, state: state?.name ?? '' }));
// // //   };

// // //   const handleShippingAddressChange = (field: keyof ShippingAddress, value: string) => {
// // //     setShippingAddress(prev => ({ ...prev, [field]: value }));
// // //   };

// // //   const handleBillingAddressChange = (field: keyof ShippingAddress, value: string) => {
// // //     setBillingAddress(prev => ({ ...prev, [field]: value }));
// // //   };

// // //   // ── Discount calculation ──────────────────────────────────────────────────
// // //   // getCartTotal() already returns the post-discount subtotal.
// // //   // discountAmount is purely informational ("you saved X") — NOT subtracted again.
// // //   const computeCartDiscountAmount = (items: CartItem[]): number =>
// // //     items.reduce((total, item) => {
// // //       if (!hasDiscount(item.discount)) return total;
// // //       const discounted = calculateDiscountedPrice(item.price, item.discount);
// // //       return total + (item.price - discounted) * item.quantity;
// // //     }, 0);

// // //   // ── Order-summary figures ─────────────────────────────────────────────────
// // //   const subtotal       = getCartTotal();
// // //   const discountAmount = computeCartDiscountAmount(cartItems);
// // //   const taxAmount      = calculateTax(subtotal);
// // //   const isFreeShipping = qualifiesForFreeShipping(subtotal, freeShippingLimit);
// // //   const finalShipping  = isFreeShipping ? 0 : shippingCost;
// // //   const totalAmount    = subtotal + taxAmount + finalShipping;

// // //   // ── Form validation ───────────────────────────────────────────────────────
// // //   const validateForm = (): boolean => {
// // //     if (!customerName.trim())                 { setError('Please enter your name');                    return false; }
// // //     if (!customerEmail.trim())                { setError('Please enter your email');                   return false; }
// // //     if (!customerPhone.trim())                { setError('Please enter your phone number');            return false; }
// // //     if (!shippingAddress.fullName.trim())     { setError('Please enter shipping address full name');  return false; }
// // //     if (!shippingAddress.addressLine1.trim()) { setError('Please enter shipping address');            return false; }
// // //     if (!shippingAddress.city.trim())         { setError('Please enter city');                        return false; }
// // //     if (!shippingAddress.postalCode.trim())   { setError('Please enter postal code');                 return false; }
// // //     if (!shippingAddress.phoneNumber.trim())  { setError('Please enter phone number for shipping');   return false; }
// // //     return true;
// // //   };

// // //   // ── Place order ───────────────────────────────────────────────────────────
// // //   const handlePlaceOrder = async (e: React.FormEvent) => {
// // //     e.preventDefault();
// // //     if (!validateForm()) return;

// // //     setLoading(true);
// // //     setError('');

// // //     try {
// // //       const orderData = {
// // //         customerName,
// // //         customerEmail,
// // //         customerPhone,
// // //         items: cartItems.map(item => ({
// // //           productId: item.productId,
// // //           variantId: item.variantId || '',
// // //           quantity:  item.quantity.toString(),
// // //         })),
// // //         subtotal:       subtotal.toString(),
// // //         taxAmount:      taxAmount.toString(),
// // //         shippingCost:   finalShipping.toString(),
// // //         discountAmount: discountAmount.toString(),
// // //         status:        'pending' as const,
// // //         paymentStatus: 'pending' as const,
// // //         paymentMethod: paymentMethod as
// // //           'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery',
// // //         shippingAddress,
// // //         billingAddress: useSameAddress ? shippingAddress : billingAddress,
// // //         notes:             orderNotes,
// // //         trackingNumber:    '',
// // //         estimatedDelivery: '',
// // //         allocatedWarehouseId: '',
// // //       };

// // //       const result = await CheckoutService.createOrder(orderData);

// // //       if (result.success) {
// // //         setSuccess(true);
// // //         clearCart();
// // //         clearShippingInfo();
// // //         setTimeout(() => navigate(`/order-confirmation/${result.orderId}`), 2000);
// // //       } else {
// // //         setError(result.message || 'Failed to place order');
// // //       }
// // //     } catch (err) {
// // //       console.error('Checkout error:', err);
// // //       setError(err instanceof Error ? err.message : 'Failed to place order. Please try again.');
// // //     } finally {
// // //       setLoading(false);
// // //     }
// // //   };

// // //   // ── Shared select style ───────────────────────────────────────────────────
// // //   const selectStyle: React.CSSProperties = {
// // //     width: '100%', padding: '10px 15px', border: 'none',
// // //     background: 'transparent', cursor: 'pointer', fontSize: '13px',
// // //     color: '#555', appearance: 'none',
// // //     fontFamily: 'Poppins-Regular, sans-serif', outline: 'none',
// // //   };

// // //   // ── Success screen ────────────────────────────────────────────────────────
// // //   if (success) {
// // //     return (
// // //       <div>
// // //         <Header />
// // //       <HeaderSpacer />
// // //         <div className="container" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
// // //           <div className="text-center">
// // //             <i className="zmdi zmdi-check-circle" style={{ fontSize: '80px', color: '#28a745', marginBottom: '20px' }} />
// // //             <h2 className="mtext-111 cl2 p-b-16">Order Placed Successfully!</h2>
// // //             <p className="stext-113 cl6 p-b-26">Thank you for your order. You will receive a confirmation email shortly.</p>
// // //             <p className="stext-113 cl6">Redirecting to order confirmation...</p>
// // //           </div>
// // //         </div>
// // //         <Footer />
// // //       </div>
// // //     );
// // //   }

// // //   return (
// // //     <div>
// // //       <Header />
// // //       <HeaderSpacer />

// // //       {/* Breadcrumb */}
// // //       <div className="container">
// // //         <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
// // //           <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
// // //             Home <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
// // //           </a>
// // //           <a href="/cart" className="stext-109 cl8 hov-cl1 trans-04">
// // //             Shopping Cart <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
// // //           </a>
// // //           <span className="stext-109 cl4">Checkout</span>
// // //         </div>
// // //       </div>

// // //       <section className="bg0 p-t-75 p-b-120">
// // //         <div className="container">
// // //           {error && (
// // //             <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '5px', color: '#721c24' }}>
// // //               {error}
// // //             </div>
// // //           )}

// // //           <form onSubmit={handlePlaceOrder}>
// // //             <div className="row">

// // //               {/* ══════════════ LEFT ══════════════ */}
// // //               <div className="col-lg-7 col-xl-8 m-lr-auto m-b-50">
// // //                 <div className="m-l-25 m-r--38 m-lr-0-xl">

// // //                   {/* Customer Information */}
// // //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
// // //                     <h4 className="mtext-109 cl2 p-b-30">Customer Information</h4>

// // //                     <div className="bor8 m-b-20 how-pos4-parent">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="text"
// // //                         placeholder="Full Name *" value={customerName} required
// // //                         onChange={e => setCustomerName(e.target.value)} />
// // //                       <i className="zmdi zmdi-account how-pos4 pointer-none" />
// // //                     </div>
// // //                     <div className="bor8 m-b-20 how-pos4-parent">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="email"
// // //                         placeholder="Email Address *" value={customerEmail} required
// // //                         onChange={e => setCustomerEmail(e.target.value)} />
// // //                       <i className="zmdi zmdi-email how-pos4 pointer-none" />
// // //                     </div>
// // //                     <div className="bor8 m-b-20 how-pos4-parent">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30" type="tel"
// // //                         placeholder="Phone Number *" value={customerPhone} required
// // //                         onChange={e => setCustomerPhone(e.target.value)} />
// // //                       <i className="zmdi zmdi-phone how-pos4 pointer-none" />
// // //                     </div>
// // //                   </div>

// // //                   {/* Shipping Address */}
// // //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
// // //                     <h4 className="mtext-109 cl2 p-b-30">Shipping Address</h4>

// // //                     <div className="bor8 m-b-20">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                         placeholder="Full Name *" value={shippingAddress.fullName} required
// // //                         onChange={e => handleShippingAddressChange('fullName', e.target.value)} />
// // //                     </div>
// // //                     <div className="bor8 m-b-20">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                         placeholder="Address Line 1 *" value={shippingAddress.addressLine1} required
// // //                         onChange={e => handleShippingAddressChange('addressLine1', e.target.value)} />
// // //                     </div>
// // //                     <div className="bor8 m-b-20">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                         placeholder="Address Line 2" value={shippingAddress.addressLine2}
// // //                         onChange={e => handleShippingAddressChange('addressLine2', e.target.value)} />
// // //                     </div>
// // //                     <div className="bor8 m-b-20">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                         placeholder="City *" value={shippingAddress.city} required
// // //                         onChange={e => handleShippingAddressChange('city', e.target.value)} />
// // //                     </div>

// // //                     {/* Country — pre-populated from Cart, select by ID, store name */}
// // //                     <div className="rs1-select2 bor8 bg0 m-b-20">
// // //                       {loadingCountries ? (
// // //                         <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
// // //                       ) : (
// // //                         <select style={selectStyle}
// // //                           value={selectedShippingCountry?.id || ''}
// // //                           onChange={e => handleShippingCountryChange(e.target.value)}>
// // //                           <option value="">Select Country *</option>
// // //                           {countries.map(c => (
// // //                             <option key={c.id} value={c.id}>{c.name}</option>
// // //                           ))}
// // //                         </select>
// // //                       )}
// // //                     </div>

// // //                     {/* State — pre-populated from Cart, select by ID, store name */}
// // //                     <div className="rs1-select2 bor8 bg0 m-b-20">
// // //                       <select style={selectStyle}
// // //                         value={selectedShippingState?.id || ''}
// // //                         onChange={e => handleShippingStateChange(e.target.value)}
// // //                         disabled={!selectedShippingCountry || shippingStates.length === 0}>
// // //                         <option value="">
// // //                           {shippingStates.length === 0 && selectedShippingCountry
// // //                             ? 'No states available'
// // //                             : 'Select State / Province'}
// // //                         </option>
// // //                         {shippingStates.map(s => (
// // //                           <option key={s.id} value={s.id}>{s.name}</option>
// // //                         ))}
// // //                       </select>
// // //                     </div>

// // //                     <div className="bor8 m-b-20">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                         placeholder="Postal Code *" value={shippingAddress.postalCode} required
// // //                         onChange={e => handleShippingAddressChange('postalCode', e.target.value)} />
// // //                     </div>
// // //                     <div className="bor8 m-b-20">
// // //                       <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
// // //                         placeholder="Phone Number *" value={shippingAddress.phoneNumber} required
// // //                         onChange={e => handleShippingAddressChange('phoneNumber', e.target.value)} />
// // //                     </div>
// // //                   </div>

// // //                   {/* Billing Address */}
// // //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 m-lr-15-sm">
// // //                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
// // //                       <h4 className="mtext-109 cl2" style={{ margin: 0 }}>Billing Address</h4>
// // //                       <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', userSelect: 'none' }}>
// // //                         <input type="checkbox" checked={useSameAddress}
// // //                           onChange={e => setUseSameAddress(e.target.checked)}
// // //                           style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }} />
// // //                         <span className="stext-107 cl2">Same as shipping</span>
// // //                       </label>
// // //                     </div>

// // //                     {!useSameAddress && (
// // //                       <>
// // //                         <div className="bor8 m-b-20">
// // //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                             placeholder="Full Name *" value={billingAddress.fullName} required
// // //                             onChange={e => handleBillingAddressChange('fullName', e.target.value)} />
// // //                         </div>
// // //                         <div className="bor8 m-b-20">
// // //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                             placeholder="Address Line 1 *" value={billingAddress.addressLine1} required
// // //                             onChange={e => handleBillingAddressChange('addressLine1', e.target.value)} />
// // //                         </div>
// // //                         <div className="bor8 m-b-20">
// // //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                             placeholder="Address Line 2" value={billingAddress.addressLine2}
// // //                             onChange={e => handleBillingAddressChange('addressLine2', e.target.value)} />
// // //                         </div>
// // //                         <div className="bor8 m-b-20">
// // //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                             placeholder="City *" value={billingAddress.city} required
// // //                             onChange={e => handleBillingAddressChange('city', e.target.value)} />
// // //                         </div>

// // //                         {/* Billing Country */}
// // //                         <div className="rs1-select2 bor8 bg0 m-b-20">
// // //                           {loadingCountries ? (
// // //                             <div className="stext-111 cl6 p-lr-15 p-tb-10">Loading countries...</div>
// // //                           ) : (
// // //                             <select style={selectStyle}
// // //                               value={selectedBillingCountry?.id || ''}
// // //                               onChange={e => handleBillingCountryChange(e.target.value)}>
// // //                               <option value="">Select Country *</option>
// // //                               {countries.map(c => (
// // //                                 <option key={c.id} value={c.id}>{c.name}</option>
// // //                               ))}
// // //                             </select>
// // //                           )}
// // //                         </div>

// // //                         {/* Billing State */}
// // //                         <div className="rs1-select2 bor8 bg0 m-b-20">
// // //                           <select style={selectStyle}
// // //                             value={selectedBillingState?.id || ''}
// // //                             onChange={e => handleBillingStateChange(e.target.value)}
// // //                             disabled={!selectedBillingCountry || billingStates.length === 0}>
// // //                             <option value="">
// // //                               {billingStates.length === 0 && selectedBillingCountry
// // //                                 ? 'No states available'
// // //                                 : 'Select State / Province'}
// // //                             </option>
// // //                             {billingStates.map(s => (
// // //                               <option key={s.id} value={s.id}>{s.name}</option>
// // //                             ))}
// // //                           </select>
// // //                         </div>

// // //                         <div className="bor8 m-b-20">
// // //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="text"
// // //                             placeholder="Postal Code *" value={billingAddress.postalCode} required
// // //                             onChange={e => handleBillingAddressChange('postalCode', e.target.value)} />
// // //                         </div>
// // //                         <div className="bor8 m-b-20">
// // //                           <input className="stext-111 cl2 plh3 size-116 p-lr-20" type="tel"
// // //                             placeholder="Phone Number *" value={billingAddress.phoneNumber} required
// // //                             onChange={e => handleBillingAddressChange('phoneNumber', e.target.value)} />
// // //                         </div>
// // //                       </>
// // //                     )}
// // //                   </div>

// // //                   {/* Order Notes */}
// // //                   <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
// // //                     <h4 className="mtext-109 cl2 p-b-30">Order Notes (Optional)</h4>
// // //                     <textarea className="stext-111 cl2 plh3 bor8 p-lr-20 p-tb-15"
// // //                       placeholder="Notes about your order, e.g. special notes for delivery."
// // //                       value={orderNotes} onChange={e => setOrderNotes(e.target.value)}
// // //                       style={{ width: '100%', minHeight: '120px', resize: 'vertical' }} />
// // //                   </div>
// // //                 </div>
// // //               </div>

// // //               {/* ══════════════ RIGHT: Order Summary ══════════════ */}
// // //               <div className="col-lg-5 col-xl-4 m-lr-auto m-b-50">
// // //                 <div className="bor10 p-lr-40 p-t-30 p-b-40 m-l-63 m-r-40 m-lr-0-xl p-lr-15-sm">
// // //                   <h4 className="mtext-109 cl2 p-b-30">Your Order</h4>

// // //                   {/* Column headers */}
// // //                   <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid #e6e6e6', marginBottom: '15px' }}>
// // //                     <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Product</span>
// // //                     <span className="stext-105 cl6" style={{ fontSize: '13px', fontWeight: '600' }}>Subtotal</span>
// // //                   </div>

// // //                   {/* Line items */}
// // //                   {cartItems.map(item => {
// // //                     const effectivePrice = calculateDiscountedPrice(item.price, item.discount);
// // //                     const showDiscount   = hasDiscount(item.discount);
// // //                     const rowTotal       = effectivePrice * item.quantity;

// // //                     return (
// // //                       <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
// // //                         <div style={{ flex: 1, paddingRight: '10px' }}>
// // //                           <div className="stext-107 cl2" style={{ fontSize: '14px', marginBottom: '4px' }}>
// // //                             {item.productName}
// // //                           </div>
// // //                           {item.variantText && (
// // //                             <div style={{ fontSize: '12px', color: '#888' }}>{item.variantText}</div>
// // //                           )}
// // //                           <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
// // //                             Qty: {item.quantity}
// // //                           </div>
// // //                           {showDiscount && (
// // //                             <div style={{ marginTop: '3px' }}>
// // //                               <span style={{ fontSize: '11px', color: '#aaa', textDecoration: 'line-through', marginRight: '6px' }}>
// // //                                 {formatPrice(item.price)}
// // //                               </span>
// // //                               <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
// // //                                 -{item.discount}%
// // //                               </span>
// // //                             </div>
// // //                           )}
// // //                         </div>
// // //                         <div style={{ fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap' }}>
// // //                           {formatPrice(rowTotal)}
// // //                         </div>
// // //                       </div>
// // //                     );
// // //                   })}

// // //                   {/* Subtotal row (post-discount) */}
// // //                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// // //                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Subtotal</span>
// // //                     <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
// // //                       {formatPrice(subtotal)}
// // //                     </span>
// // //                   </div>

// // //                   {/* Discount row — informational only, already baked into subtotal */}
// // //                   {discountAmount > 0 && (
// // //                     <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// // //                       <span style={{ fontSize: '14px', color: '#e74c3c' }}>You saved</span>
// // //                       <span style={{ fontSize: '14px', fontWeight: '500', color: '#e74c3c' }}>
// // //                         -{formatPrice(discountAmount)}
// // //                       </span>
// // //                     </div>
// // //                   )}

// // //                   {/* Tax */}
// // //                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// // //                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>
// // //                       Tax{taxPercentage > 0 ? ` (${taxPercentage}%)` : ''}
// // //                     </span>
// // //                     <span className="stext-107 cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
// // //                       {formatPrice(taxAmount)}
// // //                     </span>
// // //                   </div>

// // //                   {/* ── Shipping row — strikethrough when free ── */}
// // //                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
// // //                     <span className="stext-107 cl2" style={{ fontSize: '14px' }}>Shipping</span>
// // //                     <div style={{ textAlign: 'right' }}>
// // //                       {isFreeShipping ? (
// // //                         /* Qualifies — show strikethrough original + Free */
// // //                         <>
// // //                           <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
// // //                             {shippingCost > 0 && (
// // //                               <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: '13px' }}>
// // //                                 {formatPrice(shippingCost)}
// // //                               </span>
// // //                             )}
// // //                             <span style={{ color: '#28a745', fontWeight: '700', fontSize: '14px' }}>Free</span>
// // //                           </div>
// // //                           <div style={{ fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
// // //                             🎉 Orders over {formatPrice(freeShippingLimit)}
// // //                           </div>
// // //                         </>
// // //                       ) : shippingCost > 0 ? (
// // //                         /* Normal cost */
// // //                         <>
// // //                           <span className="cl2" style={{ fontSize: '14px', fontWeight: '500' }}>
// // //                             {formatPrice(shippingCost)}
// // //                           </span>
// // //                           {freeShippingLimit > 0 && (
// // //                             <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
// // //                               Add {formatPrice(Math.max(0, freeShippingLimit - subtotal))} more for free shipping
// // //                             </div>
// // //                           )}
// // //                         </>
// // //                       ) : (
// // //                         <span className="cl2" style={{ fontSize: '14px' }}>TBD</span>
// // //                       )}
// // //                     </div>
// // //                   </div>

// // //                   {/* Total */}
// // //                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0 25px 0', borderBottom: '2px solid #333' }}>
// // //                     <span className="mtext-110 cl2" style={{ fontSize: '16px', fontWeight: '700' }}>Total</span>
// // //                     <span className="mtext-110 cl2" style={{ fontSize: '18px', fontWeight: '700', color: '#333' }}>
// // //                       {formatPrice(totalAmount)}
// // //                     </span>
// // //                   </div>

// // //                   {/* Payment Method */}
// // //                   <div className="p-t-30 p-b-30">
// // //                     <h5 className="stext-109 cl2 p-b-15" style={{ fontSize: '15px', fontWeight: '600' }}>Payment Method</h5>
// // //                     {([
// // //                       { value: 'credit_card',     label: 'Credit Card' },
// // //                       { value: 'paypal',           label: 'PayPal' },
// // //                       { value: 'bank_transfer',    label: 'Bank Transfer' },
// // //                       { value: 'cash_on_delivery', label: 'Cash on Delivery' },
// // //                     ] as const).map(({ value, label }, idx, arr) => (
// // //                       <div key={value} className="bor8 p-lr-15 p-tb-12"
// // //                         style={{ border: '1px solid #e6e6e6', borderRadius: '4px', marginBottom: idx < arr.length - 1 ? '8px' : 0 }}>
// // //                         <label className="flex-m stext-107 cl2 pointer" style={{ fontSize: '14px', cursor: 'pointer' }}>
// // //                           <input type="radio" name="paymentMethod" value={value}
// // //                             checked={paymentMethod === value}
// // //                             onChange={e => setPaymentMethod(e.target.value)}
// // //                             style={{ marginRight: '10px' }} />
// // //                           {label}
// // //                         </label>
// // //                       </div>
// // //                     ))}
// // //                   </div>

// // //                   {/* Place Order */}
// // //                   <button type="submit"
// // //                     className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
// // //                     disabled={loading}
// // //                     style={{ opacity: loading ? 0.7 : 1, width: '100%', fontSize: '14px', padding: '16px 15px', fontWeight: '600' }}>
// // //                     {loading ? 'Processing...' : 'Place Order'}
// // //                   </button>
// // //                 </div>
// // //               </div>

// // //             </div>
// // //           </form>
// // //         </div>
// // //       </section>

// // //       <Footer />
// // //       <BackToTop />

// // //       <style>{`
// // //         .rs1-select2 { position: relative; }
// // //         .rs1-select2::after {
// // //           content: '▼'; position: absolute; right: 15px; top: 50%;
// // //           transform: translateY(-50%); font-size: 10px; color: #666; pointer-events: none;
// // //         }
// // //         .rs1-select2 select:disabled { opacity: 0.5; cursor: not-allowed; }
// // //       `}</style>
// // //     </div>
// // //   );
// // // };

// // // export default Checkout;